import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./App.css";
import {
  Pencil,
  Trash2,
  Eye,
  MessageSquare,
  Printer,
  ClipboardList,
  FileText,
  CalendarDays,
  Users,
  Settings,
  ShieldCheck,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChartNoAxesColumn,
  ClipboardCheck,
  MessageCircle,
  UserRound,
  FilePenLine,
} from "lucide-react";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { generateA4 } from "./utils/generateA4";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { BarChart, Bar, XAxis, YAxis } from "recharts";
import { startCamera, stopCamera, capturePhoto } from "./utils/camera";
import SignatureCanvas from "react-signature-canvas";
import ReactDatePicker from "./ReactDatePicker";
import { Toaster } from "react-hot-toast";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

axios.defaults.withCredentials = true;

function Admin() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [cardPrintedFilter, setCardPrintedFilter] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const activeCount = members.filter((m) => m.statut === "actif").length;
  const expiredCount = members.filter((m) => m.statut === "expire").length;
  const unknownCount = members.filter((m) => m.statut === "inconnu").length;
  const [overdueNotified, setOverdueNotified] = useState(false);
  const prevTasksRef = useRef([]);

  const [selectedIds, setSelectedIds] = useState([]);

  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfCurrent, setPdfCurrent] = useState(0);
  const [pdfTotal, setPdfTotal] = useState(0);
  const [pdfProgress, setPdfProgress] = useState(0);

  const adminSigRef = useRef();
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeBetween, setActiveBetween] = useState(null);
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [singleDate, setSingleDate] = useState("");
  const [mode, setMode] = useState("default"); // "default" | "single" | "range"
  const [distributionExpanded, setDistributionExpanded] = useState(false);

  const [allTasks, setAllTasks] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [showAppointments, setShowAppointments] = useState(false);
  const [showUsefulForms, setShowUsefulForms] = useState(false);
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [showSuiviPopup, setShowSuiviPopup] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [processingSlot, setProcessingSlot] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoRef = useRef();
  const [cropPhoto, setCropPhoto] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    x: 10,
    y: 10,
    width: 80,
    height: 80,
  });
  const cropImageRef = useRef(null);

  const usefulAssociationForms = [
    {
      title: "Aide médicale de l’État (AME)",
      description:
        "Formulaire officiel de demande d’AME — Cerfa S3720i homologué 2026, publié par l’Assurance Maladie.",
      filename: "formulaire-ame-s3720i-homologue-2026",
      url: "https://www.ameli.fr/sites/default/files/formulaires/formulaire-ame-s3720i-homologue-2026.pdf",
    },
    {
      title: "Régularisation — Métiers en tension",
      description:
        "Formulaire officiel de demande d’admission exceptionnelle au séjour au titre des métiers en tension.",
      filename: "formulaire-aes-metiers-en-tension",
      url: "https://www.prefecturedepolice.interieur.gouv.fr/sites/default/files/Documents/formulaire-aes-metiers-en-tension.pdf",
    },
    {
      title: "Demande d’aide juridictionnelle",
      description:
        "Formulaire officiel de demande d’aide juridictionnelle — Cerfa n° 16146.",
      filename: "cerfa-16146-demande-aide-juridictionnelle",
      url: "https://www.formulaires.service-public.gouv.fr/gf/cerfa_16146.do",
    },
    {
      title: "Fiche de suivi d’accompagnement",
      description:
        "Suivre les démarches, besoins, pièces manquantes et prochaines étapes d’une personne accompagnée.",
      filename: "fiche-suivi-accompagnement",
      sections: [
        [
          "Informations de la personne",
          [
            "Nom et prénom",
            "Date de naissance",
            "Téléphone / e-mail",
            "Adresse",
          ],
        ],
        [
          "Situation et demande",
          [
            "Objet de la demande",
            "Situation résumée",
            "Besoins identifiés",
            "Organismes déjà contactés",
          ],
        ],
        [
          "Suivi des démarches",
          [
            "Date",
            "Démarche effectuée",
            "Résultat / réponse",
            "Pièces remises ou manquantes",
          ],
        ],
        [
          "Prochaines étapes",
          [
            "Actions à réaliser",
            "Personne responsable",
            "Date de suivi prévue",
          ],
        ],
      ],
    },
    {
      title: "Compte rendu d’entretien",
      description:
        "Garder une trace claire d’un rendez-vous ou d’un entretien avec un bénéficiaire.",
      filename: "compte-rendu-entretien",
      sections: [
        [
          "Informations de l’entretien",
          [
            "Date et heure",
            "Lieu / mode de rencontre",
            "Nom du bénévole ou salarié",
            "Personnes présentes",
          ],
        ],
        [
          "Compte rendu",
          [
            "Objet de l’entretien",
            "Points abordés",
            "Informations communiquées",
            "Décisions prises",
          ],
        ],
        [
          "Suite à donner",
          [
            "Actions convenues",
            "Responsable de chaque action",
            "Date du prochain contact",
          ],
        ],
      ],
    },
    {
      title: "Feuille de présence",
      description:
        "Enregistrer la présence lors d’une réunion, d’un atelier ou d’une activité associative.",
      filename: "feuille-de-presence",
      sections: [
        [
          "Informations de l’activité",
          ["Nom de l’activité / réunion", "Date", "Lieu", "Responsable"],
        ],
        [
          "Présences",
          [
            "N°",
            "Nom et prénom",
            "Téléphone ou e-mail (facultatif)",
            "Signature",
          ],
        ],
      ],
    },
    {
      title: "Attestation de remise de documents",
      description:
        "Confirmer la remise ou la réception de documents entre l’association et une personne.",
      filename: "attestation-remise-documents",
      sections: [
        [
          "Informations",
          [
            "Nom et prénom de la personne",
            "Date de remise",
            "Nom du représentant de l’association",
          ],
        ],
        [
          "Documents concernés",
          ["Document 1", "Document 2", "Document 3", "Autres documents"],
        ],
        [
          "Confirmation",
          [
            "Observations / réserves",
            "Signature de la personne",
            "Signature du représentant",
          ],
        ],
      ],
    },
    {
      title: "Fiche de bénévolat",
      description:
        "Recueillir les disponibilités et les domaines d’aide proposés par un bénévole.",
      filename: "fiche-benevolat",
      sections: [
        ["Coordonnées", ["Nom et prénom", "Téléphone", "E-mail", "Ville"]],
        [
          "Disponibilités et compétences",
          [
            "Disponibilités",
            "Compétences / langues",
            "Missions souhaitées",
            "Expériences utiles",
          ],
        ],
        [
          "Organisation",
          ["Date de début", "Référent associatif", "Observations", "Signature"],
        ],
      ],
    },
    {
      title: "Demande de rendez-vous",
      description:
        "Formulaire simple pour recueillir le motif d’une demande et organiser le suivi.",
      filename: "demande-rendez-vous",
      sections: [
        [
          "Coordonnées",
          ["Nom et prénom", "Téléphone", "E-mail", "Langue souhaitée"],
        ],
        [
          "Demande",
          [
            "Motif du rendez-vous",
            "Disponibilités",
            "Documents disponibles",
            "Urgence / délai souhaité",
          ],
        ],
        [
          "Réservé à l’association",
          ["Date du rendez-vous proposé", "Intervenant", "Suite donnée"],
        ],
      ],
    },
  ];

  const downloadUsefulForm = (form) => {
    // Official forms are opened from their source; internal templates are generated locally.
    if (form.url) {
      window.open(form.url, "_blank", "noopener,noreferrer");
      return;
    }
    const escapeHtml = (value) =>
      String(value).replace(
        /[&<>"']/g,
        (char) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          })[char],
      );
    const sections = form.sections
      .map(
        ([heading, fields]) => `
      <section>
        <h2>${escapeHtml(heading)}</h2>
        ${fields
          .map(
            (field, index) => `
          <div class="field">
            <label>${escapeHtml(field)}</label>
            ${
              form.filename === "feuille-de-presence" && heading === "Présences"
                ? `<div class="lines"></div><div class="lines"></div><div class="lines"></div><div class="lines"></div><div class="lines"></div>`
                : `<div class="write-line"></div>`
            }
          </div>
        `,
          )
          .join("")}
      </section>
    `,
      )
      .join("");
    const html = `<!doctype html>
<html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escapeHtml(form.title)}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#1b2d49;margin:0;background:#fff}
main{max-width:850px;margin:0 auto;padding:32px}
header{border-bottom:3px solid #f26a21;padding-bottom:16px;margin-bottom:24px}
.brand{font-size:12px;letter-spacing:2px;color:#63738d;font-weight:bold;text-transform:uppercase}
h1{font-size:25px;margin:10px 0;color:#10284d}p{color:#66758d;font-size:13px}
section{margin:22px 0;break-inside:avoid}h2{font-size:15px;background:#f0f4fa;border-left:4px solid #f26a21;padding:10px;margin:0 0 12px}
.field{margin:12px 0;font-size:12px;font-weight:bold}.write-line{height:27px;border-bottom:1px solid #aab6c8;margin-top:4px}
.lines{height:34px;border-bottom:1px solid #aab6c8}
.signature{display:flex;gap:35px;margin-top:26px}.signature div{flex:1;font-size:12px;font-weight:bold}.signature span{display:block;height:65px;border-bottom:1px solid #8b98ac}
footer{margin-top:30px;padding-top:10px;border-top:1px solid #d9e0ea;color:#8490a4;font-size:10px}
@media print{main{padding:0;max-width:none}body{margin:12mm}h2{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
</style></head><body><main><header><div class="brand">Document interne · Association</div><h1>${escapeHtml(form.title)}</h1><p>À compléter lors de l’utilisation. Conserver ce document selon les règles internes de confidentialité de l’association.</p></header>
${sections}
<div class="signature"><div>Fait le : <span></span></div><div>Nom et signature : <span></span></div></div>
<footer>Modèle pratique à adapter aux procédures de l’association. Ce document est un modèle interne et ne remplace pas un formulaire officiel lorsqu’il est requis.</footer>
</main></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${form.filename}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin");
    localStorage.removeItem("member");
    navigate("/");
  };

  const fetchUnread = async () => {
    try {
      const res = await axios.get("/api/getUnreadMessages.php");

      setUnreadMessages(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUnread();

    const interval = setInterval(fetchUnread, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadAppointments = async () => {
    try {
      console.log("🚀 loadAppointments CALLED");

      const res = await axios.get("/api/getAllAppointments.php");

      console.log("APPOINTMENTS RES:", res.data);

      // ✅ Always normalize to array
      let data = [];

      if (Array.isArray(res.data)) {
        data = res.data;
      } else if (res.data && Array.isArray(res.data.appointments)) {
        data = res.data.appointments;
      } else if (res.data && Array.isArray(res.data.data)) {
        data = res.data.data;
      }

      // ✅ Set state (single source of truth)
      setAppointments(data);
    } catch (err) {
      console.error("LOAD APPOINTMENTS ERROR:", err);
      setAppointments([]); // fallback
    }
  };

  const fetchSlots = async (date) => {
    try {
      const res = await axios.get(`/api/getAvailableSlots.php?date=${date}`);
      setSlots(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("SLOTS ERROR:", err);
      setSlots([]);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, []);
  useEffect(() => {
    axios
      .get("/api/getAllTasks.php")
      .then((res) => {
        // 🔥 FORCE ARRAY
        let tasks = [];

        if (Array.isArray(res.data)) {
          tasks = res.data;
        } else if (res.data && Array.isArray(res.data.tasks)) {
          tasks = res.data.tasks;
        }

        // 🔥 CLEAN EACH TASK
        const cleanTasks = tasks.map((t) => ({
          ...t,
          due_date: t.due_date ? t.due_date.split(" ")[0] : null,
          status: (t.status || "").toLowerCase(),
        }));

        console.log("CLEAN TASKS:", cleanTasks);

        setAllTasks(cleanTasks);
        checkOverdueTasks(cleanTasks);
        prevTasksRef.current = cleanTasks;
      })
      .catch((err) => {
        console.error("TASK LOAD ERROR:", err);
        setAllTasks([]);
      });
  }, []);

  const getTaskStatus = (task) => {
    if (task.status === "done") return "done";

    if (!task.due_date) return "active";

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // 🔥 SAFE DATE PARSE
    const [year, month, day] = task.due_date.split("-");
    const due = new Date(year, month - 1, day);

    due.setHours(0, 0, 0, 0);

    console.log("NOW:", now);
    console.log("DUE:", due);
    console.log("IS OVERDUE:", due < now);
    console.log("TASK:", task.title);

    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "expired";
    if (diffDays <= 2) return "soon";

    return "active";
  };

  //CALCULATE COUNTS

  const counts = React.useMemo(() => {
    const c = { active: 0, soon: 0, expired: 0, done: 0 };

    allTasks.forEach((task) => {
      const status = getTaskStatus(task);

      if (status === "done") c.done++;
      else if (status === "expired") c.expired++;
      else if (status === "soon") c.soon++;
      else c.active++;
    });

    console.log("COUNTS:", c);

    return c;
  }, [allTasks]);

  const [alertedTasks, setAlertedTasks] = useState([]);

  const checkOverdueTasks = (tasks) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // 🔥 get overdue tasks
    const overdue = tasks.filter((task) => {
      if (!task.due_date || task.status === "done") return false;

      const [year, month, day] = task.due_date.split("-");
      const due = new Date(year, month - 1, day);
      due.setHours(0, 0, 0, 0);

      return due < now;
    });

    console.log("OVERDUE:", overdue);

    // ✅ SHOW ONLY ONE TOAST
    if (overdue.length > 0 && !overdueNotified) {
      toast.custom((t) => (
        <div
          onClick={() => {
            setSelectedType("expired");
            toast.dismiss(t.id);
          }}
          className="pro-toast"
        >
          <div className="pro-toast-icon">⚠️</div>

          <div className="pro-toast-content">
            <div className="pro-toast-title">
              {overdue.length} tâche(s) en retard
            </div>

            <div className="pro-toast-sub">Cliquer pour voir les tâches</div>
          </div>

          <div className="pro-toast-arrow">→</div>
        </div>
      ));
      setOverdueNotified(true);
    }
  };

  const hasTasksChanged = (oldTasks, newTasks) => {
    if (oldTasks.length !== newTasks.length) return true;

    for (let i = 0; i < newTasks.length; i++) {
      if (
        oldTasks[i]?.id !== newTasks[i]?.id ||
        oldTasks[i]?.status !== newTasks[i]?.status ||
        oldTasks[i]?.due_date !== newTasks[i]?.due_date
      ) {
        return true;
      }
    }

    return false;
  };

  const [flash, setFlash] = useState(false);

  useEffect(() => {
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 300);
    return () => clearTimeout(t);
  }, [counts]);

  useEffect(() => {
    const interval = setInterval(() => {
      axios
        .get("/api/getAllTasks.php")
        .then((res) => {
          let tasks = Array.isArray(res.data) ? res.data : [];

          const cleanTasks = tasks.map((t) => ({
            ...t,
            due_date: t.due_date ? t.due_date.split(" ")[0] : null,
            status: (t.status || "").trim().toLowerCase(),
          }));

          if (hasTasksChanged(prevTasksRef.current, cleanTasks)) {
            console.log("🔄 TASKS CHANGED");

            setAllTasks(cleanTasks);
            checkOverdueTasks(cleanTasks);

            prevTasksRef.current = cleanTasks;
          } else {
            console.log("⏸ No change");
          }
          setOverdueNotified(false);
        })
        .catch(console.error);
    }, 5000); // ⏱ every 5 sec

    return () => clearInterval(interval);
  }, []);

  const formatDate = (date) => {
    if (!date || date === "0000-00-00") return "—";

    const d = new Date(date);

    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  //ANALYTICS: SHOW RANGE MODAL
  const [rangeStats, setRangeStats] = useState(null);
  const [showRangeModal, setShowRangeModal] = useState(false);
  const fetchRangeStats = async () => {
    try {
      const res = await axios.get(
        `/api/getRangeAnalytics.php?from=${dateFrom}&to=${dateTo}`,
      );

      console.log("RANGE RESPONSE:", res.data);

      setRangeStats(res.data);

      setShowRangeModal(true);
    } catch (err) {
      console.error(err);
    }
  };

  const [selectedList, setSelectedList] = useState([]);
  const [listTitle, setListTitle] = useState("");

  const [analytics, setAnalytics] = useState({
    membersPerMonth: [],
    renewalsPerMonth: [],
    status: {},
  });

  const sendCardByEmail = async (member) => {
    const pdf = new jsPDF();

    // 👉 YOUR REAL DESIGN HERE
    pdf.text(`${member.nom} ${member.prenom}`, 20, 20);
    pdf.text(`Numéro: ${member.numero_membre}`, 20, 30);

    // 🔥 CONVERT PDF TO BLOB
    const pdfBlob = pdf.output("blob");

    const formData = new FormData();
    formData.append("pdf", pdfBlob, "carte.pdf");
    formData.append("email", member.email);

    await axios.post("/api/sendCard.php", formData);
  };

  //preview current signature

  const [currentSignature, setCurrentSignature] = useState(null);

  useEffect(() => {
    axios.get("/api/getAdminSignature.php").then((res) => {
      setCurrentSignature(res.data.signature);
    });
  }, []);

  // =========================
  // 🔍 FILTER (status)
  // =========================
  const [statusFilter, setStatusFilter] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    actif: 0,
    expire: 0,
    inconnu: 0,
  });

  //SINGLE DATE

  const applySingleDate = () => {
    if (!singleDate) return alert("Choisir une date");

    const date = singleDate;

    setMode("single");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setPage(1);

    fetchStats(date);
    fetchMembers({ date });
  };

  // FETCH MEMBERS
  const fetchMembers = async (custom = {}) => {
    setLoading(true);

    const from = custom.from ?? (mode === "range" ? dateFrom : "");
    const to = custom.to ?? (mode === "range" ? dateTo : "");
    const date =
      custom.date !== undefined
        ? custom.date
        : mode === "single"
          ? singleDate
          : null;
    const status = custom.status ?? statusFilter;

    try {
      const res = await axios.get(
        `/api/getMembers.php?page=${page}&search=${encodeURIComponent(search)}&status=${status}&from=${from}&to=${to}&card_printed=${cardPrintedFilter}${date ? `&date=${date}` : ""}`,
      );

      setMembers(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };
  // Statut d’impression des cartes : indépendant du statut d’adhésion.
  const updateCardPrinted = async (id, printed) => {
    try {
      const res = await axios.post("/api/updateCardPrinted.php", {
        action: "set",
        id,
        printed: printed ? 1 : 0,
      });
      if (res.data?.success === false)
        throw new Error(res.data.message || "Mise à jour refusée");
      setMembers((prev) =>
        prev.map((member) =>
          String(member.id) === String(id)
            ? { ...member, carte_imprimee: printed ? 1 : 0 }
            : member,
        ),
      );
      toast.success(
        printed
          ? "Carte marquée comme imprimée"
          : "Carte marquée comme non imprimée",
      );
    } catch (err) {
      console.error("CARD PRINT STATUS ERROR:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Impossible de modifier le statut de la carte",
      );
    }
  };

  const markAllCardsPrinted = async () => {
    const password = window.prompt(
      "Mot de passe pour marquer toutes les cartes comme imprimées :",
    );
    if (password === null) return;
    if (password !== "123") {
      toast.error("Mot de passe incorrect");
      return;
    }
    if (
      !window.confirm(
        "Confirmer : marquer les cartes de TOUS les membres de la base comme imprimées ? Cette action ne dépend pas du filtre actuel.",
      )
    )
      return;
    const toastId = toast.loading("Mise à jour des cartes...");
    try {
      const res = await axios.post("/api/updateCardPrinted.php", {
        action: "mark_all",
        password,
      });
      if (res.data?.success === false)
        throw new Error(res.data.message || "Mise à jour refusée");
      toast.success(
        `${res.data.updated ?? "Toutes les"} cartes marquées comme imprimées`,
        { id: toastId },
      );
      await fetchMembers();
    } catch (err) {
      console.error("MARK ALL CARDS ERROR:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Impossible de mettre à jour les cartes",
        { id: toastId },
      );
    }
  };

  const fetchStats = async (customDate = null) => {
    try {
      let url = "/api/getStats.php";

      if (customDate) {
        url += `?date=${customDate}`;
      } else if (mode === "range" && dateFrom && dateTo) {
        url += `?from=${dateFrom}&to=${dateTo}`;
      }

      const res = await axios.get(url);
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // useEffect(() => {
  //   if (selectedMember) {
  //     loadComments(selectedMember.id);
  //   }
  // }, [selectedMember]);

  // Delete member
  const deleteMember = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;

    const toastId = toast.loading("Suppression...");

    try {
      await axios.post("/api/deleteMember.php", { id });

      toast.success("Membre supprimé 🗑️", { id: toastId });
      fetchMembers();
    } catch (err) {
      console.error(err);
      toast.error("Erreur suppression ❌", { id: toastId });
    }
  };
  //UPDATE MEMBER
  const [editing, setEditing] = useState(null);

  const handleAdhesionChange = (date) => {
    setEditing((prev) => ({
      ...prev,
      adhesion: date,
    }));
  };

  const getDaysLeft = (date, referenceDate = null) => {
    const ref = referenceDate ? new Date(referenceDate) : new Date();
    const exp = new Date(date);

    ref.setHours(0, 0, 0, 0);
    exp.setHours(0, 0, 0, 0);

    return Math.ceil((exp - ref) / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    fetchStats();
  }, [dateFrom, dateTo]);
  // console.log("SEARCH:", search);
  // console.log("MEMBERS:", members);
  // console.log("FILTERED:", filtered);

  // COMMENTS
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState("");
  const activeCommentEditButton = {
    border: 0,
    borderRadius: 6,
    padding: "5px 8px",
    background: "#1554ed",
    color: "#fff",
    fontWeight: 700,
    fontSize: 11,
    cursor: "pointer",
  };
  // Translation is kept only in the UI; original comments in the database are never changed.
  const [commentTranslations, setCommentTranslations] = useState({});
  const [translatingComments, setTranslatingComments] = useState({});

  const translateComment = async (comment, targetLanguage) => {
    const commentId = String(
      comment.id ??
        comment.comment_id ??
        comment.created_at ??
        comment.commentaire,
    );
    const originalText = String(comment.commentaire || "").trim();
    if (!originalText) return;

    const cached = commentTranslations[commentId] || {};
    if (cached[targetLanguage]) {
      setCommentTranslations((prev) => ({
        ...prev,
        [commentId]: { ...prev[commentId], selectedLanguage: targetLanguage },
      }));
      return;
    }

    setTranslatingComments((prev) => ({ ...prev, [commentId]: true }));
    try {
      // Google Translate's public web endpoint; requires an internet connection.
      const params = new URLSearchParams({
        client: "gtx",
        sl: "auto",
        tl: targetLanguage,
        dt: "t",
        q: originalText,
      });
      const response = await fetch(
        `https://translate.googleapis.com/translate_a/single?${params.toString()}`,
      );
      if (!response.ok)
        throw new Error(`Translation request failed (${response.status})`);
      const result = await response.json();
      const translatedText = Array.isArray(result?.[0])
        ? result[0].map((part) => (Array.isArray(part) ? part[0] : "")).join("")
        : "";
      if (!translatedText) throw new Error("Empty translation response");
      setCommentTranslations((prev) => ({
        ...prev,
        [commentId]: {
          ...prev[commentId],
          [targetLanguage]: translatedText,
          selectedLanguage: targetLanguage,
        },
      }));
    } catch (error) {
      console.error("COMMENT TRANSLATION ERROR:", error);
      toast.error(
        "Traduction impossible. Vérifiez votre connexion internet et réessayez.",
      );
    } finally {
      setTranslatingComments((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  const loadComments = async (memberId) => {
    try {
      const res = await axios.get(`/api/getComments.php?id=${memberId}`);

      const data = Array.isArray(res.data) ? res.data : res.data.data || [];

      setComments(data);
    } catch (err) {
      console.error("LOAD COMMENTS ERROR:", err);
      setComments([]); // fallback
    }
  };

  //🖨 EXPORT ALL
  const exportAll = async () => {
    if (pdfExporting) return;

    try {
      const res = await axios.get("/api/getAllMembers.php");

      const allMembers = res.data.data || [];

      if (!allMembers.length) {
        alert("Aucun membre");
        return;
      }

      setPdfExporting(true);
      setPdfCurrent(0);
      setPdfTotal(allMembers.length);
      setPdfProgress(0);

      await generateA4(allMembers, (current, total) => {
        setPdfCurrent(current);
        setPdfTotal(total);

        const percent = total > 0 ? Math.round((current / total) * 100) : 0;

        setPdfProgress(percent);
      });
    } catch (err) {
      console.error("PDF EXPORT ERROR:", err);
      alert("Erreur export");
    } finally {
      setPdfExporting(false);
    }
  };
  //EXPORT SELECTED
  const exportSelected = async () => {
    if (pdfExporting) return;

    if (selectedIds.length === 0) {
      alert("Aucun membre sélectionné");
      return;
    }

    try {
      const res = await axios.post("/api/getMembersByIds.php", {
        ids: selectedIds,
      });

      const selectedMembers = res.data.data || [];

      if (!selectedMembers.length) {
        alert("Aucun membre trouvé");
        return;
      }

      setPdfExporting(true);
      setPdfCurrent(0);
      setPdfTotal(selectedMembers.length);
      setPdfProgress(0);

      await generateA4(selectedMembers, (current, total) => {
        setPdfCurrent(current);
        setPdfTotal(total);

        const percent = total > 0 ? Math.round((current / total) * 100) : 0;

        setPdfProgress(percent);
      });
    } catch (err) {
      console.error("PDF EXPORT ERROR:", err);
      alert("Erreur export");
    } finally {
      setPdfExporting(false);
    }
  };
  useEffect(() => {
    fetchMembers({
      status: statusFilter,
      date: mode === "single" ? singleDate : null,
      from: mode === "range" ? dateFrom : "",
      to: mode === "range" ? dateTo : "",
    });
  }, [
    page,
    search,
    statusFilter,
    mode,
    singleDate,
    dateFrom,
    dateTo,
    cardPrintedFilter,
  ]);

  //RANGE PARTENS
  const formatPattern = (pattern) => {
    return pattern
      .replaceAll("actif", "🟢 Actif")
      .replaceAll("expire", "🔴 Expiré")
      .replaceAll("-", " → ");
  };

  useEffect(() => {
    console.log("🚀 ADMIN LOADED");
  }, []);

  useEffect(() => {
    console.log("🔥 TEST FETCH");

    axios
      .get("/api/getAllMembers.php", {
        withCredentials: true,
      })
      .then((res) => console.log("MEMBERS:", res.data))
      .catch((err) => console.error("MEMBERS ERROR:", err));

    axios
      .get("/api/getAllTasks.php", {
        withCredentials: true,
      })
      .then((res) => console.log("TASKS:", res.data))
      .catch((err) => console.error("TASKS ERROR:", err));
  }, []);

  let suiviEdit = editing?.suivi_administratif;

  try {
    if (typeof suiviEdit === "string" && suiviEdit.startsWith("[")) {
      suiviEdit = JSON.parse(suiviEdit);
    }
  } catch {}

  if (!Array.isArray(suiviEdit)) {
    suiviEdit = suiviEdit ? [suiviEdit] : [];
  }

  const createCroppedPhoto = async () => {
    if (!cropImageRef.current || !cropPhoto) {
      return;
    }

    const image = cropImageRef.current;

    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;

    const cropX = (crop.x / 100) * naturalWidth;

    const cropY = (crop.y / 100) * naturalHeight;

    const cropWidth = (crop.width / 100) * naturalWidth;

    const cropHeight = (crop.height / 100) * naturalHeight;

    if (cropWidth <= 0 || cropHeight <= 0) {
      alert("Veuillez sélectionner une zone à recadrer.");
      return;
    }

    const canvas = document.createElement("canvas");

    canvas.width = Math.round(cropWidth);
    canvas.height = Math.round(cropHeight);

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          alert("Impossible de recadrer la photo.");
          return;
        }

        const croppedFile = new File(
          [blob],
          cropPhoto.name || "member-photo.jpg",
          {
            type: "image/jpeg",
            lastModified: Date.now(),
          },
        );

        const previewUrl = URL.createObjectURL(croppedFile);

        /*
         * Replace the original photo with
         * the CROPPED photo.
         */
        setEditing((prev) => ({
          ...prev,
          newPhoto: croppedFile,
          preview: previewUrl,
        }));

        setPhotoPreview(previewUrl);

        /*
         * Close crop window.
         */
        setCropPhoto(null);

        /*
         * Reset crop for the next photo.
         */
        setCrop({
          unit: "%",
          x: 10,
          y: 10,
          width: 75,
          height: 100,
        });
      },
      "image/jpeg",
      0.92,
    );
  };

  return (
    <>
      <Toaster position="top-left" />

      {pdfExporting && (
        <div className="pdf-loading-overlay">
          <div className="pdf-loading-modal">
            <div className="pdf-loading-icon">🖨️</div>

            <h2>Génération du PDF</h2>

            <p>Veuillez patienter pendant la génération des cartes...</p>

            <div className="pdf-progress-info">
              <strong>
                Carte {pdfCurrent} sur {pdfTotal}
              </strong>

              <span>{pdfProgress}%</span>
            </div>

            <div className="pdf-progress-bar">
              <div
                className="pdf-progress-fill"
                style={{
                  width: `${pdfProgress}%`,
                }}
              />
            </div>

            <p className="pdf-loading-small">Ne fermez pas cette fenêtre.</p>
          </div>
        </div>
      )}

      <div className="admin-layout">
        <aside
          className={`admin-sidebar${sidebarCollapsed ? " sidebar-collapsed" : ""}`}
        >
          <div className="sidebar-brand">
            <img
              className="sidebar-logo"
              src="/logo.png"
              alt="United Migrants"
            />
            <button
              type="button"
              className="sidebar-collapse"
              aria-label="Réduire le menu"
              onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            >
              {sidebarCollapsed ? (
                <ChevronRight size={21} strokeWidth={2.2} />
              ) : (
                <ChevronLeft size={21} strokeWidth={2.2} />
              )}
            </button>
          </div>

          <button
            type="button"
            className="sidebar-back"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft size={18} />
            <span>Retour</span>
          </button>

          <nav className="sidebar-nav" aria-label="Navigation principale">
            <button
              type="button"
              className="sidebar-card active"
              onClick={() => navigate("/form")}
            >
              <FilePenLine size={21} />
              <span>Inscription</span>
            </button>
            <button
              type="button"
              className="sidebar-card"
              onClick={() => setShowUsefulForms(true)}
            >
              <FileText size={21} />
              <span>Formulaires</span>
            </button>
            <button
              type="button"
              className="sidebar-card"
              onClick={() => setShowNotif(true)}
            >
              <MessageSquare size={21} />
              <span>Messages</span>
              {unreadMessages.length > 0 && (
                <span className="sidebar-badge">{unreadMessages.length}</span>
              )}
            </button>
            <button
              type="button"
              className="sidebar-card"
              onClick={() => setShowAppointments(true)}
            >
              <CalendarDays size={21} />
              <span>Rendez-vous</span>
            </button>
            <button type="button" className="sidebar-card" onClick={exportAll}>
              <Printer size={21} />
              <span>Exporter / Imprimer</span>
            </button>
            <button
              type="button"
              className="sidebar-card"
              onClick={exportSelected}
            >
              <ClipboardList size={21} />
              <span>
                Cartes <small>({selectedIds.length})</small>
              </span>
            </button>
            {admin?.role === "super_admin" && (
              <>
                <div className="sidebar-divider" />
                <button
                  type="button"
                  className="sidebar-card"
                  onClick={() => navigate("/super-admin")}
                >
                  <ShieldCheck size={21} />
                  <span>Administration</span>
                  <ChevronRight className="sidebar-chevron" size={17} />
                </button>
                <button
                  type="button"
                  className="sidebar-card"
                  onClick={() => navigate("/settings")}
                >
                  <Settings size={21} />
                  <span>Paramètres</span>
                </button>
              </>
            )}
          </nav>

          <div className="sidebar-bottom">
            <div className="sidebar-profile">
              <div className="sidebar-avatar">
                <UserRound size={25} />
              </div>
              <div className="sidebar-profile-text">
                <strong>
                  {admin?.name || admin?.username || "Administrateur"}
                </strong>
                <span>
                  {admin?.role === "super_admin"
                    ? "Super administrateur"
                    : "Administrateur"}
                </span>
              </div>
              <ChevronRight size={18} />
            </div>
            <button
              type="button"
              className="sidebar-logout"
              onClick={handleLogout}
            >
              <LogOut size={19} />
              <span>Se déconnecter</span>
            </button>
          </div>
        </aside>

        <main className="admin-content">
          {showSignatureEditor && (
            <div
              className="modal-overlay"
              onClick={() => setShowSignatureEditor(false)}
            >
              <div
                className="signature-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <h3>Modifier la signature</h3>

                {/* PREVIEW */}
                {currentSignature && (
                  <img
                    src={`/api/image.php?file=${currentSignature}`}
                    alt="signature"
                    className="signature-preview"
                  />
                )}

                {/* INPUT */}
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const formData = new FormData();
                    formData.append("signature", file);

                    try {
                      await axios.post(
                        "/api/saveAdminSignature.php",
                        formData,
                        { withCredentials: true },
                      );

                      const res = await axios.get(
                        "/api/getAdminSignature.php",
                        {
                          withCredentials: true,
                        },
                      );

                      setCurrentSignature(res.data.signature);

                      setShowSignatureEditor(false);

                      alert("Signature mise à jour ✅");
                    } catch (err) {
                      console.error(err);
                      alert("Erreur ❌");
                    }
                  }}
                />

                {/* CLOSE BUTTON */}
                <button
                  className="btn secondary"
                  onClick={() => setShowSignatureEditor(false)}
                  style={{ marginTop: 10 }}
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
          {/* HEADER */}
          <div className="dashboard-welcome flex flex-col xl:flex-row gap-4 mb-6">
            {/* LEFT */}

            <div
              className="
  bg-white
  rounded-2xl
  shadow-sm
  border
  border-slate-200
  p-4
  mb-6
  flex
  flex-col
  lg:flex-row
  lg:items-center
  lg:justify-between
  gap-4
  "
            >
              <div>
                <h1 className="text-3xl font-bold text-slate-800">Dashboard</h1>

                <p className="text-slate-500 mt-1">
                  Gestion des membres • {total} inscrits
                </p>
              </div>

              <div
                className="
    flex
    flex-wrap
    items-center
    gap-2
    "
              >
                <div className="notif-container">
                  {showNotif && (
                    <div className="notif-dropdown">
                      {unreadMessages.length === 0 ? (
                        <p>Aucun message</p>
                      ) : (
                        unreadMessages.map((m) => (
                          <div
                            key={m.member_id}
                            className="notif-item"
                            onClick={async () => {
                              try {
                                await axios.post(
                                  "/api/markAsRead.php",
                                  { member_id: m.member_id },
                                  { withCredentials: true },
                                );
                              } catch (err) {
                                console.error("markAsRead failed:", err);
                              }

                              // ✅ ALWAYS RUN
                              setShowNotif(false);
                              navigate(`/member/${m.member_id}`);
                              fetchUnread();
                            }}
                          >
                            <strong>
                              {m.prenom} {m.nom}
                            </strong>
                            <p>{m.unread_count} message(s)</p>
                            <small>
                              {new Date(m.last_message).toLocaleString()}
                            </small>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/*Appointments on same line as Tasks */}

            <div
              className="
  grid
  grid-cols-1
  xl:grid-cols-3
  gap-4
  mb-6
  "
            >
              <div className="task-section">
                <div className="dashboard-section">
                  <h2 className="task-section-title">📋 Suivi des tâches</h2>
                  <div
                    className="
 task-grid"
                  >
                    <div
                      className="task-card-pro green"
                      onClick={() => setSelectedType("active")}
                    >
                      <div className="task-card-icon">🟢</div>
                      <div>
                        <h4>En cours</h4>
                        <p>{counts.active}</p>
                      </div>
                    </div>

                    <div
                      className="task-card-pro orange"
                      onClick={() => setSelectedType("soon")}
                    >
                      <div className="task-card-icon">🟠</div>
                      <div>
                        <h4>Bientôt</h4>
                        <p>{counts.soon}</p>
                      </div>
                    </div>

                    <div
                      className="task-card-pro red"
                      onClick={() => setSelectedType("expired")}
                    >
                      <div className="task-card-icon">🔴</div>
                      <div>
                        <h4>En retard</h4>
                        <p>{counts.expired}</p>
                      </div>
                    </div>

                    <div
                      className="task-card-pro blue"
                      onClick={() => setSelectedType("done")}
                    >
                      <div className="task-card-icon">✅</div>
                      <div>
                        <h4>Terminées</h4>
                        <p>{counts.done}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedType && (
              <div className="modal-overlay">
                <div className="modal task-modal">
                  <div className="modal-header">
                    <h3>📋 Tâches</h3>
                    <button onClick={() => setSelectedType(null)}>✖</button>
                  </div>

                  <div className="task-list">
                    {allTasks
                      .filter((task) =>
                        selectedType === "done"
                          ? task.status === "done"
                          : getTaskStatus(task) === selectedType,
                      )
                      .map((task) => {
                        const status = getTaskStatus(task);

                        return (
                          <div
                            key={task.id}
                            className={`task-card ${getTaskStatus(task)}`}
                            onClick={() =>
                              navigate(`/member/${task.member_id}`)
                            }
                          >
                            <div className="task-left">
                              <div className={`status-dot ${status}`} />

                              <div>
                                <strong className="task-title">
                                  {task.title}
                                </strong>

                                {task.description && (
                                  <p className="task-desc">
                                    {task.description}
                                  </p>
                                )}

                                <span className="task-member">
                                  👤 {task.nom} {task.prenom}
                                </span>
                              </div>
                            </div>

                            <div className="task-right">
                              {task.due_date && (
                                <span className="task-date">
                                  📅 {task.due_date}
                                </span>
                              )}

                              <span className={`task-badge ${status}`}>
                                {status === "expired" && "🔴 En retard"}
                                {status === "soon" && "🟠 Bientôt"}
                                {status === "active" && "🟢 En cours"}
                                {status === "done" && "✅ Terminé"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}

            {/* RIGHT */}

            {showUsefulForms && (
              <div
                className="modal-overlay"
                onClick={() => setShowUsefulForms(false)}
              >
                <div
                  className="modal useful-forms-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Formulaires utiles"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="useful-forms-header">
                    <div>
                      <span className="useful-forms-eyebrow">
                        ESPACE ASSOCIATION
                      </span>
                      <h2>Formulaires utiles</h2>
                      <p>
                        Des modèles pratiques à télécharger pour les activités
                        quotidiennes de l’association.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="useful-forms-close"
                      onClick={() => setShowUsefulForms(false)}
                      aria-label="Fermer"
                    >
                      ×
                    </button>
                  </div>
                  <div className="useful-forms-grid">
                    {usefulAssociationForms.map((form) => (
                      <article className="useful-form-card" key={form.filename}>
                        <div className="useful-form-icon">📄</div>
                        <div className="useful-form-copy">
                          <h3>{form.title}</h3>
                          <p>{form.description}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadUsefulForm(form)}
                        >
                          {form.url
                            ? "↗ Ouvrir le formulaire officiel"
                            : "↓ Télécharger"}
                        </button>
                      </article>
                    ))}
                  </div>
                  <div className="useful-forms-note">
                    Les fichiers téléchargés sont des modèles HTML imprimables.
                    Ouvre-les dans ton navigateur puis utilise Imprimer →
                    Enregistrer au format PDF si tu souhaites un PDF.
                  </div>
                </div>
              </div>
            )}

            {showAppointments && (
              <div
                className="modal-overlay"
                onClick={() => setShowAppointments(false)}
              >
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                  <div className="rdv-header-actions">
                    <button
                      className="btn danger"
                      onClick={() => setShowBlockModal(true)}
                    >
                      🚫 Bloquer des créneaux
                    </button>

                    <button onClick={() => setShowAppointments(false)}>
                      ✖
                    </button>
                  </div>

                  <div className="modal-header">
                    <h3>📅 Rendez-vous</h3>
                    <button onClick={() => setShowAppointments(false)}>
                      ✖
                    </button>
                  </div>

                  {Array.isArray(appointments) && appointments.length > 0 ? (
                    <table className="appointments-table">
                      <thead>
                        <tr>
                          <th>Nom complet</th>
                          <th>Date</th>
                          <th>Heure</th>
                          <th>Action</th> {/* 🔥 NEW */}
                        </tr>
                      </thead>

                      <tbody>
                        {appointments.map((a) => (
                          <tr
                            key={a.id}
                            className={`clickable-row ${a.status === "blocked" ? "blocked-row" : ""}`}
                          >
                            <td
                              onClick={() => navigate(`/member/${a.member_id}`)}
                            >
                              {a.prenom} {a.nom}
                            </td>

                            <td>{a.date}</td>
                            <td>{a.time}</td>

                            {/* ❌ CANCEL */}
                            <td>
                              {a.status !== "cancelled" ? (
                                <button
                                  className="btn small danger"
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    if (
                                      !window.confirm(
                                        "Annuler ce rendez-vous ?",
                                      )
                                    )
                                      return;

                                    await axios.post(
                                      "/api/cancelAppointment.php",
                                      {
                                        id: a.id,
                                      },
                                    );

                                    toast.success("Rendez-vous annulé ❌");
                                    loadAppointments();
                                  }}
                                >
                                  ❌ Annuler
                                </button>
                              ) : (
                                <span className="cancelled-label">Annulé</span>
                              )}
                            </td>

                            {/* 🚫 BLOCK / UNBLOCK */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="empty">Aucun rendez-vous</p>
                  )}
                </div>
              </div>
            )}

            {showBlockModal && (
              <div
                className="modal-overlay"
                onClick={() => setShowBlockModal(false)}
              >
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>🚫 Bloquer des créneaux</h3>
                    <button onClick={() => setShowBlockModal(false)}>✖</button>
                  </div>

                  {/* DATE PICKER */}
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      fetchSlots(e.target.value);
                    }}
                  />

                  {/* SLOTS */}
                  <div className="slots">
                    {slots.map((s) => {
                      const isBlocked = s.blocked;
                      const isFull = !s.available;
                      const isSelected = selectedSlots.includes(s.time);

                      return (
                        <button
                          key={s.time}
                          className={`slot-btn
                ${isSelected ? "active" : ""}
                ${isFull ? "full" : ""}
                ${isBlocked ? "blocked" : ""}
              `}
                          onClick={async () => {
                            if (processingSlot === s.time) return;

                            // 🚫 BLOCKED → UNBLOCK
                            if (isBlocked) {
                              const confirmUnblock = window.confirm(
                                `⚠️ Ce créneau (${s.time}) est bloqué.\nVoulez-vous le débloquer ?`,
                              );

                              if (!confirmUnblock) return;

                              try {
                                setProcessingSlot(s.time);

                                await axios.post("/api/unblockSlot.php", {
                                  date: selectedDate,
                                  time: s.time,
                                });

                                toast.success(`Créneau ${s.time} débloqué ✅`);

                                await fetchSlots(selectedDate);
                                await loadAppointments();
                              } catch (err) {
                                console.error(err);
                                toast.error("Erreur lors du déblocage ❌");
                              } finally {
                                setProcessingSlot(null);
                              }

                              return;
                            }

                            // ❌ FULL → DO NOTHING
                            if (isFull) return;

                            // ✅ SELECT / UNSELECT
                            if (isSelected) {
                              setSelectedSlots(
                                selectedSlots.filter((t) => t !== s.time),
                              );
                            } else {
                              setSelectedSlots([...selectedSlots, s.time]);
                            }
                          }}
                        >
                          {s.time}

                          <span className="slot-count">{s.count}/3</span>

                          {isBlocked && (
                            <span className="blocked-label">
                              🚫 Bloqué (cliquer pour débloquer)
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* ACTION BUTTON */}
                  <button
                    className="btn danger"
                    onClick={async () => {
                      if (selectedSlots.length === 0) {
                        return alert("Choisir au moins un créneau");
                      }

                      await axios.post("/api/blockMultipleSlots.php", {
                        date: selectedDate,
                        slots: selectedSlots,
                      });

                      toast.success("Créneaux bloqués 🚫");

                      setSelectedSlots([]);
                      fetchSlots(selectedDate);
                      loadAppointments();
                    }}
                  >
                    🚫 Bloquer sélection ({selectedSlots.length})
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* CHART BAR */}

          <div
            className={`dashboard-top ${distributionExpanded ? "distribution-expanded" : "distribution-collapsed"}`}
          >
            {/* COLLAPSIBLE TITLE */}
            <button
              type="button"
              className="distribution-toggle"
              aria-expanded={distributionExpanded}
              onClick={() => setDistributionExpanded((expanded) => !expanded)}
            >
              <span className="distribution-toggle-copy">
                <strong>📊 Répartition des membres</strong>
                <small>Analyse rapide des inscriptions et statuts</small>
              </span>
              <span
                className={`distribution-chevron ${distributionExpanded ? "is-open" : ""}`}
                aria-hidden="true"
              >
                ⌄
              </span>
            </button>

            {distributionExpanded && (
              <>
                {/* FILTERS */}
                <div className="toolbar-card">
                  <div className="filters-card">
                    {/* SINGLE DATE */}
                    <div
                      className={`stats-single-box ${mode === "single" ? "active" : ""}`}
                    >
                      <label>📅 Date précise</label>

                      <ReactDatePicker
                        value={singleDate}
                        onChange={(date) => {
                          if (!date) return;
                          setSingleDate(date.toISOString().split("T")[0]);
                        }}
                      />

                      <button className="btn primary" onClick={applySingleDate}>
                        Voir stats
                      </button>
                    </div>

                    {/* 🔥 RESET (CENTER) */}
                    <div className="reset-container">
                      <button
                        className="btn reset-btn"
                        onClick={() => {
                          setMode("default");
                          setDateFrom("");
                          setDateTo("");
                          setSingleDate("");
                          setStatusFilter("");
                          setPage(1);

                          fetchStats();
                          fetchMembers({});
                        }}
                      >
                        🔄 Reset
                      </button>
                    </div>

                    {/* RANGE */}
                    <div
                      className={`filter-block ${mode === "range" ? "active" : ""}`}
                    >
                      <label>📆 Période</label>

                      <div className="range-row">
                        <ReactDatePicker
                          value={dateFrom}
                          onChange={(date) => {
                            if (!date) return;
                            setDateFrom(date.toISOString().split("T")[0]);
                          }}
                        />

                        <span>→</span>

                        <ReactDatePicker
                          value={dateTo}
                          onChange={(date) => {
                            if (!date) return;
                            setDateTo(date.toISOString().split("T")[0]);
                          }}
                        />
                      </div>

                      <button
                        className="btn primary"
                        onClick={() => {
                          if (!dateFrom || !dateTo) {
                            return alert("Choisir les dates");
                          }

                          // 🔥 FIX: prevent negative period
                          if (new Date(dateFrom) > new Date(dateTo)) {
                            return alert(
                              "La date de début doit être avant la date de fin ❌",
                            );
                          }

                          setMode("range");
                          fetchRangeStats();
                        }}
                      >
                        🔍
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="
grid
grid-cols-1
md:grid-cols-3
gap-4
mb-6
"
                >
                  <div
                    className={`card total ${statusFilter === "" ? "active-card" : ""}`}
                    onClick={() => {
                      setStatusFilter("");
                      setPage(1);

                      fetchMembers({
                        status: "",
                        date: mode === "single" ? singleDate : null,
                        from: mode === "range" ? dateFrom : "",
                        to: mode === "range" ? dateTo : "",
                      });
                    }}
                  >
                    <h3>Total</h3>
                    <p>{stats.total}</p>
                  </div>

                  <div
                    className={`card active ${statusFilter === "actif" ? "active-card" : ""}`}
                    onClick={() => {
                      const newStatus = "actif";

                      setStatusFilter(newStatus);
                      setPage(1);

                      fetchMembers({
                        status: newStatus,
                        date: mode === "single" ? singleDate : null,
                        from: mode === "range" ? dateFrom : "",
                        to: mode === "range" ? dateTo : "",
                      });
                    }}
                  >
                    <h3>Actifs</h3>
                    <p>{stats.actif}</p>
                  </div>

                  <div
                    className={`card expired ${statusFilter === "expire" ? "active-card" : ""}`}
                    onClick={() => {
                      const newStatus = "expire";

                      setStatusFilter(newStatus);
                      setPage(1);

                      fetchMembers({
                        status: newStatus,
                        date: mode === "single" ? singleDate : null,
                        from: mode === "range" ? dateFrom : "",
                        to: mode === "range" ? dateTo : "",
                      });
                    }}
                  >
                    <h3>Expirés</h3>
                    <p>{stats.expire}</p>
                  </div>
                </div>
              </>
            )}

            {/* SEARCH BAR */}
            <div className="member-search-toolbar">
              <input
                type="text"
                placeholder="Rechercher un membre…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Rechercher un membre"
                className="member-search-input"
              />
              <select
                value={cardPrintedFilter}
                onChange={(e) => {
                  setCardPrintedFilter(e.target.value);
                  setPage(1);
                }}
                aria-label="Filtrer les cartes imprimées ou à imprimer"
                style={{
                  padding: "10px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  background: "#fff",
                  minHeight: 42,
                }}
              >
                <option value="">Toutes les cartes</option>
                <option value="0">Cartes à imprimer</option>
                <option value="1">Cartes imprimées</option>
              </select>
              <button
                type="button"
                onClick={markAllCardsPrinted}
                style={{
                  padding: "10px 14px",
                  border: 0,
                  borderRadius: 8,
                  background: "#166534",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  minHeight: 42,
                }}
              >
                <Printer
                  size={16}
                  style={{
                    display: "inline",
                    marginRight: 6,
                    verticalAlign: "middle",
                  }}
                />
                Marquer toutes les cartes imprimées
              </button>
            </div>
          </div>
          {/* MEMBER LIST DESIGN: styles are scoped to this table only */}
          <style>{`
            .members-card { background:#fff; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; box-shadow:0 8px 28px rgba(15,23,42,.055); }
            .members-card > div:first-child { background:linear-gradient(180deg,#fff 0%,#f8fafc 100%); }
            .members-card .member-list-heading { min-height:42px; }
            .members-card .member-list-heading h2 { margin:0; font-size:16px !important; }
            .members-card .member-list-heading span { padding:4px 8px; }
            .members-card > div:first-child h2 { color:#172033; letter-spacing:-.025em; }
            .members-card > div:first-child span { display:inline-flex; align-items:center; padding:6px 11px; border-radius:999px; background:#fff7ed; color:#c2410c; border:1px solid #fed7aa; font-size:12px; font-weight:700; }
            .member-table-scroll { width:100%; overflow-x:auto; }
            .members-card table.member-list-table { width:100%; min-width:1120px; border-collapse:separate; border-spacing:0; color:#334155; font-size:13px; }
            .members-card table.member-list-table thead th { position:sticky; top:0; z-index:1; background:#fff7ed; color:#9a3412; font-size:11px; text-transform:uppercase; letter-spacing:.055em; font-weight:800; padding:13px 12px; border-bottom:1px solid #fed7aa; white-space:nowrap; }
            .members-card table.member-list-table tbody td { padding:10px 12px; border-bottom:1px solid #edf1f5; vertical-align:middle; white-space:nowrap; }
            .members-card table.member-list-table tbody tr { transition:background .15s ease; }
            .members-card table.member-list-table tbody tr:nth-child(even) { background:#fbfcfe; }
            .members-card table.member-list-table tbody tr:hover { background:#fff7ed; }
            .members-card table.member-list-table tbody tr:last-child td { border-bottom:0; }
            .members-card table.member-list-table tbody td:nth-child(3) { color:#0f172a; font-weight:700; }
            .members-card table.member-list-table tbody td:nth-child(5) { color:#475569; font-weight:700; font-variant-numeric:tabular-nums; }
            .members-card table.member-list-table tbody td:nth-child(2) img { display:block; width:44px; height:44px; object-fit:cover; border-radius:12px; border:2px solid #fff; box-shadow:0 0 0 1px #fdba74,0 3px 8px rgba(15,23,42,.10); }
            .members-card table.member-list-table tbody td:last-child { text-align:center; white-space:nowrap; }
            .members-card table.member-list-table tbody td:last-child button { display:inline-flex; flex:0 0 34px; align-items:center; justify-content:center; width:34px; height:34px; min-width:34px; padding:0; margin:0; border:1px solid #e8edf3; border-radius:10px; background:#fff; color:#475569; box-shadow:0 1px 3px rgba(15,23,42,.05); cursor:pointer; transition:all .15s ease; }
            .members-card table.member-list-table tbody td:last-child button:hover { transform:translateY(-1px); border-color:#fdba74; background:#fff7ed; color:#c2410c; box-shadow:0 4px 10px rgba(194,65,12,.10); }
            .members-card table.member-list-table tbody td:last-child button.icon-btn.edit { color:#2563eb; }
            .members-card table.member-list-table tbody td:last-child button.icon-btn.edit:hover { background:#eff6ff; border-color:#bfdbfe; color:#1d4ed8; }
            .members-card table.member-list-table tbody td:last-child button .tooltip { display:none; }
            .members-card table.member-list-table .status-badge { display:inline-flex; align-items:center; justify-content:center; gap:5px; border-radius:999px; padding:6px 10px; font-size:11px; font-weight:750; white-space:nowrap; }
            .members-card table.member-list-table .status-badge.active { color:#047857; background:#ecfdf5; border:1px solid #a7f3d0; }
            .members-card table.member-list-table .status-badge.warning { color:#b45309; background:#fffbeb; border:1px solid #fde68a; }
            .members-card table.member-list-table .status-badge.expired { color:#b91c1c; background:#fef2f2; border:1px solid #fecaca; }
            .members-card .pagination { display:flex; align-items:center; justify-content:center; gap:14px; padding:16px; border-top:1px solid #edf1f5; background:#fbfcfe; }
            .members-card .pagination button { display:inline-flex; align-items:center; justify-content:center; min-width:36px; height:36px; border:1px solid #e2e8f0; border-radius:10px; background:#fff; }
            .members-card .pagination button:disabled { opacity:.4; cursor:not-allowed; }
            /* DASHBOARD HEADER + FILTERS: scoped visual refresh */
            .dashboard-welcome { margin-bottom:8px !important; }
            .dashboard-welcome > div { min-height:0 !important; border-radius:12px !important; border:1px solid #e2e8f0 !important; background:linear-gradient(135deg,#ffffff 0%,#fffaf5 100%) !important; box-shadow:0 3px 10px rgba(15,23,42,.035) !important; padding:10px 16px !important; margin-bottom:0 !important; gap:8px !important; }
            .dashboard-welcome h1 { color:#172033 !important; font-size:22px !important; line-height:1.15; letter-spacing:-.03em; text-align:left !important; margin:0 !important; }
            .dashboard-welcome p { color:#64748b !important; font-size:12px !important; margin:3px 0 0 !important; }
            .dashboard-top { display:flex; flex-direction:column; gap:6px !important; margin-top:8px !important; }
            .dashboard-top .distribution-toggle { width:100%; display:flex; align-items:center; justify-content:space-between; gap:12px; padding:8px 12px; border:1px solid #e2e8f0; border-radius:12px; background:#fff; color:#172033; text-align:left; cursor:pointer; box-shadow:0 2px 8px rgba(15,23,42,.025); }
            .dashboard-top .distribution-toggle:hover { background:#fffaf5; border-color:#fed7aa; }
            .dashboard-top .distribution-toggle-copy { display:flex; flex-direction:column; gap:2px; }
            .dashboard-top .distribution-toggle-copy strong { font-size:16px; line-height:1.25; font-weight:800; letter-spacing:-.02em; }
            .dashboard-top .distribution-toggle-copy small { color:#64748b; font-size:11px; font-weight:400; }
            .dashboard-top .distribution-chevron { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; flex:0 0 28px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#475569; font-size:22px; line-height:1; transition:transform .18s ease, background .18s ease; }
            .dashboard-top .distribution-chevron.is-open { transform:rotate(180deg); background:#fff7ed; border-color:#fed7aa; color:#c2410c; }
            .dashboard-top.distribution-collapsed { gap:6px !important; margin-bottom:4px !important; }
            .dashboard-top.distribution-expanded { gap:8px !important; }
            .dashboard-top .top-header { padding:2px 2px 0; }
            .dashboard-top .top-header h2 { margin:0; color:#172033; font-size:18px !important; font-weight:800; letter-spacing:-.025em; }
            .dashboard-top .top-header p { margin-top:2px !important; color:#64748b; font-size:11px !important; }
            .dashboard-top .toolbar-card { padding:10px 12px !important; border:1px solid #e2e8f0 !important; border-radius:12px !important; background:#fff !important; box-shadow:0 3px 10px rgba(15,23,42,.03) !important; }
            .dashboard-top .filters-card { display:grid !important; grid-template-columns:minmax(150px,.8fr) auto minmax(260px,1.2fr) !important; align-items:end !important; gap:10px !important; }
            .dashboard-top .stats-single-box, .dashboard-top .filter-block { display:flex; flex-direction:column; gap:5px !important; min-width:0; padding:0 !important; border:0 !important; background:transparent !important; }
            .dashboard-top .stats-single-box label, .dashboard-top .filter-block label { color:#334155 !important; font-size:12px !important; font-weight:750 !important; }
            .dashboard-top .stats-single-box input, .dashboard-top .filter-block input { width:100%; min-height:32px; border:1px solid #dbe3ed !important; border-radius:10px !important; background:#f8fafc !important; padding:9px 12px !important; color:#1e293b !important; }
            .dashboard-top .stats-single-box button, .dashboard-top .filter-block > button { min-height:31px; border-radius:10px !important; font-weight:700 !important; }
            .dashboard-top .reset-container { display:flex; align-items:center; justify-content:center; padding-bottom:1px; }
            .dashboard-top .reset-btn { min-height:31px; padding:0 16px !important; border-radius:10px !important; background:#f1f5f9 !important; color:#334155 !important; border:1px solid #e2e8f0 !important; font-weight:700 !important; }
            .dashboard-top .range-row { display:grid !important; grid-template-columns:minmax(0,1fr) auto minmax(0,1fr); align-items:center; gap:8px; }
            .dashboard-top .range-row span { color:#94a3b8; }
            .dashboard-top .card { position:relative; display:flex !important; align-items:center; justify-content:space-between; min-height:54px !important; padding:10px 14px !important; border:1px solid rgba(226,232,240,.85) !important; border-radius:16px !important; box-shadow:0 6px 18px rgba(15,23,42,.07) !important; transition:transform .16s ease, box-shadow .16s ease; cursor:pointer; }
            .dashboard-top .card:hover { transform:translateY(-2px); box-shadow:0 10px 24px rgba(15,23,42,.10) !important; }
            .dashboard-top .card h3 { margin:0; font-size:13px !important; font-weight:700 !important; opacity:.9; }
            .dashboard-top .card p { margin:0; font-size:21px !important; font-weight:850 !important; letter-spacing:-.04em; }
            .dashboard-top .card.total { background:#1e293b !important; color:#fff !important; }
            .dashboard-top .card.active { background:#16a765 !important; color:#fff !important; }
            .dashboard-top .card.expired { background:#ef4444 !important; color:#fff !important; }
            .dashboard-top .card.active-card { outline:3px solid rgba(249,115,22,.24); outline-offset:2px; }
            .dashboard-top .bg-white.rounded-2xl { padding:6px 8px !important; margin-bottom:4px !important; border-radius:12px !important; box-shadow:none !important; }
            .member-search-toolbar { width:100%; display:flex; justify-content:center; align-items:center; margin:10px 0 14px; padding:0 12px; box-sizing:border-box; }
            .member-search-input { display:block; width:min(100%, 760px) !important; max-width:760px !important; min-height:46px; box-sizing:border-box; border:1px solid #cbd5e1 !important; border-radius:14px !important; background:#fff !important; padding:0 18px !important; font-size:14px !important; color:#0f172a !important; box-shadow:0 4px 14px rgba(15,23,42,.055); outline:none; transition:border-color .18s ease, box-shadow .18s ease; }
            .member-search-input:focus { border-color:#f97316 !important; box-shadow:0 0 0 3px rgba(249,115,22,.14), 0 5px 16px rgba(15,23,42,.06); }
            .member-search-input::placeholder { color:#94a3b8; }
            @media (max-width: 600px) { .member-search-toolbar { padding:0 4px; margin:8px 0 12px; } .member-search-input { width:100% !important; min-height:42px; } }
            .task-section { margin-bottom:0 !important; }
            .task-section .dashboard-section { padding:0 !important; background:transparent !important; box-shadow:none !important; }
            .task-section .task-section-title { margin:0 0 5px !important; color:#334155 !important; font-size:14px !important; font-weight:800 !important; }
            .task-section .task-grid { gap:8px !important; }
            .task-section .task-card-pro { min-height:48px !important; padding:8px 12px !important; border-radius:13px !important; box-shadow:0 3px 12px rgba(15,23,42,.04) !important; }
            @media (max-width: 900px) { .dashboard-top .filters-card { grid-template-columns:1fr !important; } .dashboard-top .reset-container { justify-content:flex-start; } .dashboard-top .card { min-height:48px !important; } .dashboard-welcome > div { padding:10px 12px !important; } }
            @media (max-width: 900px) { .members-card { border-radius:14px; } .members-card table.member-list-table thead th { padding:11px 9px; } .members-card table.member-list-table tbody td { padding:9px; } }

            /* ===== Professional blue admin dashboard refresh ===== */
            .admin-layout {
              display:flex !important; align-items:stretch !important; min-height:100vh !important;
              width:100% !important; background:#f3f6fa !important; color:#e7edf6; gap:0 !important;
            }
            .admin-sidebar {
              position:sticky !important; top:0 !important; align-self:flex-start !important;
              display:flex !important; flex-direction:column !important; flex:0 0 218px !important;
              width:218px !important; min-width:218px !important; height:100vh !important;
              min-height:100vh !important; box-sizing:border-box !important; padding:10px 9px 9px !important;
              background:linear-gradient(180deg,#0b1728 0%,#101d30 100%) !important;
              color:#e7edf6 !important; border-right:1px solid rgba(226,232,240,.16) !important;
              border-radius:0 !important; box-shadow:none !important; overflow-y:auto !important;
              gap:5px !important; z-index:2147483000 !important; pointer-events:auto !important; isolation:isolate !important;
            }
            .admin-sidebar, .admin-sidebar * { box-sizing:border-box; }
            .admin-sidebar { justify-content:flex-start !important; }
            .sidebar-back { flex:0 0 32px !important; height:32px !important; margin:0 0 12px 5px !important; }
            .sidebar-nav { flex:0 0 auto !important; margin:0 !important; padding:0 !important; }
            .sidebar-nav .sidebar-card { flex:0 0 auto !important; }
            .sidebar-brand { display:flex; align-items:center; justify-content:center; position:relative; flex:0 0 76px; min-height:76px; height:76px; margin:0 0 12px !important; padding:0 30px 0 0; }
            .sidebar-logo { display:block !important; flex:0 0 58px !important; width:58px !important; height:58px !important; max-width:58px !important; object-fit:contain !important; object-position:center; border-radius:50%; border:2px solid rgba(255,255,255,.92); background:#fff; padding:2px; position:relative; z-index:1; }
            .sidebar-collapse { position:absolute !important; right:0 !important; top:17px !important; z-index:3 !important; display:flex !important; align-items:center !important; justify-content:center !important; width:25px !important; height:27px !important; border:0 !important; border-radius:6px !important; background:#243247 !important; color:#d7e0ed !important; cursor:pointer !important; padding:0 !important; }
            .sidebar-back {
              display:flex !important; align-items:center !important; justify-content:flex-start !important; gap:9px !important;
              width:max-content !important; min-width:0 !important; min-height:30px !important; margin:0 0 10px 5px !important;
              padding:5px 3px !important; border:0 !important; border-radius:0 !important;
              background:transparent !important; color:#cbd5e1 !important; font-size:12px !important; font-weight:500 !important;
              cursor:pointer !important;
            }
            .sidebar-back:hover { background:transparent !important; color:#fff !important; text-decoration:underline; }
            .sidebar-nav { display:flex; flex-direction:column; gap:5px; width:100%; }
            .sidebar-card {
              display:flex !important; flex-direction:row !important; align-items:center !important; justify-content:flex-start !important; gap:10px !important;
              min-height:42px !important; width:100% !important; margin:0 !important; padding:9px 11px !important;
              border:1px solid transparent !important; border-radius:9px !important; background:transparent !important;
              color:#d4deeb !important; font-family:inherit !important; font-size:13px !important; font-weight:550 !important;
              line-height:1.25 !important; text-align:left !important; cursor:pointer !important;
              transition:background .15s ease,color .15s ease !important;
            }
            .sidebar-card svg { display:block !important; flex:0 0 21px !important; width:21px !important; height:21px !important; color:#d2dce9; stroke-width:1.9; }
            .admin-sidebar .sidebar-card { flex-direction:row !important; white-space:nowrap !important; }
            .admin-sidebar .sidebar-card > svg, .admin-sidebar .sidebar-card > span { align-self:center !important; }
            .sidebar-card > span:not(.sidebar-badge) { display:inline-flex !important; align-items:center !important; flex:1 1 auto !important; min-width:0 !important; white-space:nowrap !important; }
            .sidebar-card small { display:inline; font-size:10px; color:#aab8ca; margin-left:3px; }
            .sidebar-card:hover { background:rgba(255,255,255,.065) !important; color:#fff !important; }
            .sidebar-card:hover svg { color:#fff; }
            .sidebar-card.active, .sidebar-card[aria-current="page"] {
              background:linear-gradient(100deg,#ff8b24,#ff791b) !important; color:#fff !important;
              box-shadow:0 3px 10px rgba(255,125,31,.16) !important;
            }
            .sidebar-card.active svg, .sidebar-card[aria-current="page"] svg { color:#fff !important; }
            .sidebar-badge {
              display:inline-flex !important; align-items:center !important; justify-content:center !important;
              min-width:21px !important; height:21px !important; margin-left:auto !important; padding:0 6px !important;
              border-radius:999px !important; background:#ff403b !important; color:#fff !important;
              font-size:11px !important; font-weight:700 !important;
            }
            .sidebar-divider { height:1px; width:calc(100% - 4px); margin:12px 2px 10px; background:rgba(203,218,240,.19); }
            .sidebar-chevron { margin-left:auto; }
            .sidebar-bottom {
              margin-top:auto !important; flex:0 0 auto !important; width:100% !important;
              display:flex !important; flex-direction:column !important; gap:8px !important;
              padding-top:12px !important; padding-bottom:4px !important;
            }
            .sidebar-profile {
              display:flex; align-items:center; gap:10px; margin-top:0 !important; padding:12px 6px 12px;
              border-top:1px solid rgba(203,218,240,.18); color:#e8eef7; min-width:0; flex:0 0 auto;
            }
            .sidebar-avatar { display:flex; align-items:center; justify-content:center; flex:0 0 34px; width:34px; height:34px; border-radius:50%; background:#34445b; color:#dce5f1; overflow:hidden; }
            .sidebar-profile-text { display:flex; flex:1; flex-direction:column; gap:3px; min-width:0; }
            .sidebar-profile-text strong { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:12px; font-weight:650; color:#f4f7fb; }
            .sidebar-profile-text span { font-size:11px; color:#a5b3c7; }
            .sidebar-logout {
              display:flex !important; align-items:center !important; justify-content:flex-start !important; gap:10px !important;
              flex:0 0 auto !important; min-height:34px !important; width:100% !important; margin:0 0 2px !important; padding:8px 10px !important;
              border:0 !important; border-radius:0 !important; background:transparent !important; color:#cbd5e1 !important;
              font-family:inherit !important; font-size:13px !important; font-weight:600 !important; cursor:pointer !important;
            }
            .sidebar-logout:hover { background:transparent !important; color:#fff !important; text-decoration:underline; }
            .sidebar-logout svg { flex:0 0 19px !important; width:19px !important; height:19px !important; }
            .sidebar-back svg { flex:0 0 16px !important; width:16px !important; height:16px !important; }
            .admin-content {
              flex:1 1 auto !important; width:calc(100% - 218px) !important; min-width:0 !important;
              box-sizing:border-box !important; padding:22px !important; background:#f3f6fa !important;
            }
            .admin-sidebar.sidebar-collapsed {
              flex-basis:70px !important; width:70px !important; min-width:70px !important;
              padding:10px 7px 9px !important;
            }
            .admin-sidebar.sidebar-collapsed .sidebar-brand { padding:0 !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-logo { width:43px !important; height:43px !important; flex-basis:43px !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-collapse { right:-1px !important; top:17px !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-back { width:100% !important; justify-content:center !important; margin-left:0 !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-back span,
            .admin-sidebar.sidebar-collapsed .sidebar-card > span:not(.sidebar-badge),
            .admin-sidebar.sidebar-collapsed .sidebar-profile-text,
            .admin-sidebar.sidebar-collapsed .sidebar-profile > svg,
            .admin-sidebar.sidebar-collapsed .sidebar-logout span,
            .admin-sidebar.sidebar-collapsed .sidebar-chevron { display:none !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-card { justify-content:center !important; padding:9px 5px !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-card svg { margin:0 auto !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-badge { position:absolute !important; right:0 !important; top:0 !important; min-width:15px !important; height:15px !important; padding:0 3px !important; font-size:9px !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-bottom { margin-top:auto !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-profile { justify-content:center !important; padding:12px 0 !important; margin-top:0 !important; }
            .admin-sidebar.sidebar-collapsed .sidebar-logout { justify-content:center !important; padding:8px 0 !important; }
            .admin-sidebar.sidebar-collapsed + .admin-content { width:calc(100% - 70px) !important; }
            @media (max-width: 650px) { .admin-layout { display:block !important; } .sidebar-collapse { display:none !important; } .admin-sidebar.sidebar-collapsed { width:100% !important; min-width:0 !important; } .admin-sidebar.sidebar-collapsed .sidebar-card > span:not(.sidebar-badge), .admin-sidebar.sidebar-collapsed .sidebar-back span, .admin-sidebar.sidebar-collapsed .sidebar-profile-text, .admin-sidebar.sidebar-collapsed .sidebar-logout span { display:inline-flex !important; } .sidebar-profile { margin-top:10px !important; } .admin-sidebar { position:relative !important; width:100% !important; min-width:0 !important; height:auto !important; min-height:0 !important; padding:12px !important; flex-direction:row !important; flex-wrap:wrap !important; align-items:center !important; } .sidebar-title { width:100%; } .sidebar-title::after { display:none; } .sidebar-back { margin:4px 0 !important; } .sidebar-card { min-height:36px !important; padding:8px !important; } .sidebar-logout { margin:0 !important; border:0 !important; } .admin-content { width:100% !important; padding:10px !important; } .task-section .task-grid { grid-template-columns:repeat(2,minmax(0,1fr)) !important; } .dashboard-welcome h1 { font-size:21px !important; } }

            /* Open a member dossier by clicking anywhere on the row.
               Interactive controls inside the row keep their own actions. */
            .members-card table.member-list-table tbody tr.clickable-member-row { cursor:pointer; }
            .members-card table.member-list-table tbody tr.clickable-member-row:focus-visible { outline:2px solid #3b82f6; outline-offset:-2px; }
            .members-card table.member-list-table tbody tr.clickable-member-row:hover { background:#eaf2ff !important; }
            /* Sidebar navigation remains visible and clearly interactive. */
            .admin-sidebar .sidebar-back, .admin-sidebar .sidebar-card, .admin-sidebar .sidebar-logout { user-select:none; }
          `}</style>
          {/* TABLE */}
          <div className="members-card">
            <div className="member-list-heading flex items-center justify-between border-b border-slate-200 px-4 py-2">
              <h2 className="text-xl font-semibold">Membres</h2>

              <span>{total} résultats</span>
            </div>
            {loading ? (
              <div className="loader">Chargement...</div>
            ) : (
              <div className="member-table-scroll">
                <table className="member-list-table min-w-[1200px] w-full">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            members.length > 0 &&
                            members.every((m) => selectedIds.includes(m.id))
                          }
                          onChange={(e) => {
                            const currentPageIds = members.map((m) => m.id);

                            if (e.target.checked) {
                              // Select all members on THIS page while keeping
                              // selections already made on other pages.
                              setSelectedIds((prev) =>
                                Array.from(
                                  new Set([...prev, ...currentPageIds]),
                                ),
                              );
                            } else {
                              // Unselect only members on THIS page.
                              setSelectedIds((prev) =>
                                prev.filter(
                                  (id) => !currentPageIds.includes(id),
                                ),
                              );
                            }
                          }}
                        />
                      </th>
                      <th>Photo</th>
                      <th>Nom</th>
                      <th>Prénom</th>
                      <th>N° Membre</th>
                      <th>N° Dossier</th>
                      <th>Contact</th>
                      <th>Adhesion</th>
                      <th>Expiration</th>
                      <th>Statut</th>
                      <th>Carte imprimée</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {members.map((m) => {
                      // ✅ 1. Get reference date (selected date or today)
                      const referenceDate =
                        mode === "single" && singleDate ? singleDate : null;

                      // ✅ 2. Compute days left (based on THAT date)
                      const daysLeft = getDaysLeft(
                        m.dateExpiration,
                        referenceDate,
                      );

                      // ✅ 3. Determine status (UI logic)
                      let statusClass = "";
                      let label = "";

                      if (daysLeft < 0) {
                        statusClass = "expired";
                        label = "🔴 Expiré";
                      } else if (daysLeft <= 30) {
                        statusClass = "warning";
                        label = "🟠 Expire bientôt";
                      } else {
                        statusClass = "active";
                        label = "🟢 Actif";
                      }
                      const isActive = daysLeft > 30;

                      return (
                        <tr
                          key={m.id || m.numero_membre}
                          className="clickable-member-row"
                          role="link"
                          tabIndex={0}
                          title={`Ouvrir le dossier de ${m.prenom || ""} ${m.nom || ""}`.trim()}
                          onClick={(event) => {
                            if (
                              event.target.closest(
                                "button, input, a, select, textarea, label",
                              )
                            )
                              return;
                            navigate(`/member/${m.id}`);
                          }}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              navigate(`/member/${m.id}`);
                            }
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(m.id)}
                              onChange={() => {
                                if (selectedIds.includes(m.id)) {
                                  setSelectedIds(
                                    selectedIds.filter((id) => id !== m.id),
                                  );
                                } else {
                                  setSelectedIds([...selectedIds, m.id]);
                                }
                              }}
                            />
                          </td>
                          <td>
                            {m.photo && (
                              <img
                                src={`/api/image.php?file=${m.photo}`}
                                alt="profil"
                              />
                            )}
                          </td>
                          <td>{m.nom}</td>
                          <td>{m.prenom}</td>
                          <td>{m.numero_membre}</td>
                          <td>{m.numero_dossier || "-"}</td>
                          <td>{m.contact}</td>
                          <td>{formatDate(m.adhesion)}</td>
                          <td>{formatDate(m.dateExpiration)}</td>

                          {/* STATUS */}
                          <td>
                            <span className={`status-badge ${statusClass}`}>
                              {label}
                            </span>
                          </td>

                          {/* CARD PRINTING STATUS */}
                          <td>
                            <button
                              type="button"
                              onClick={() =>
                                updateCardPrinted(
                                  m.id,
                                  Number(m.carte_imprimee) !== 1,
                                )
                              }
                              title={
                                Number(m.carte_imprimee) === 1
                                  ? "Marquer comme non imprimée"
                                  : "Marquer comme imprimée"
                              }
                              aria-label={
                                Number(m.carte_imprimee) === 1
                                  ? "Marquer la carte comme non imprimée"
                                  : "Marquer la carte comme imprimée"
                              }
                              style={{
                                border: "1px solid",
                                borderColor:
                                  Number(m.carte_imprimee) === 1
                                    ? "#86efac"
                                    : "#fdba74",
                                background:
                                  Number(m.carte_imprimee) === 1
                                    ? "#f0fdf4"
                                    : "#fff7ed",
                                color:
                                  Number(m.carte_imprimee) === 1
                                    ? "#166534"
                                    : "#9a3412",
                                borderRadius: 999,
                                padding: "6px 10px",
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {Number(m.carte_imprimee) === 1
                                ? "✓ Imprimée"
                                : "À imprimer"}
                            </button>
                          </td>

                          {/* REMINDER */}
                          <td>
                            <button
                              type="button"
                              title={
                                m.reminder_sent
                                  ? "Rappel déjà envoyé"
                                  : "Envoyer un rappel"
                              }
                              aria-label={
                                m.reminder_sent
                                  ? "Rappel déjà envoyé"
                                  : "Envoyer un rappel"
                              }
                              onClick={async () => {
                                if (!window.confirm("Envoyer un rappel ?"))
                                  return;

                                const formData = new FormData();
                                formData.append("type", "reminder");
                                formData.append("id", m.id);
                                formData.append("email", m.email);
                                formData.append("nom", m.nom);
                                formData.append("prenom", m.prenom);
                                formData.append(
                                  "dateExpiration",
                                  m.dateExpiration,
                                );

                                await axios.post("/api/sendCard.php", formData);

                                toast.success("📧 Rappel envoyé !");

                                setMembers((prev) =>
                                  prev.map((member) =>
                                    member.id === m.id
                                      ? { ...member, reminder_sent: 1 }
                                      : member,
                                  ),
                                );
                              }}
                            >
                              {m.reminder_sent ? "✅" : "⏰"}
                            </button>
                            <button
                              type="button"
                              title="Modifier"
                              aria-label="Modifier le membre"
                              className="icon-btn edit"
                              onClick={() => {
                                let suivi = m.suivi_administratif;

                                try {
                                  if (
                                    typeof suivi === "string" &&
                                    suivi.startsWith("[")
                                  ) {
                                    suivi = JSON.parse(suivi);
                                  } else if (typeof suivi === "string") {
                                    suivi = [suivi]; // convert old single value
                                  }
                                } catch {
                                  suivi = [];
                                }

                                setEditing({
                                  ...m,
                                  suivi_administratif: suivi,
                                });
                              }}
                            >
                              <Pencil size={16} />
                              <span className="tooltip">Modifier</span>
                            </button>

                            <button
                              type="button"
                              title="Supprimer"
                              aria-label="Supprimer le membre"
                              className="icon-btn note"
                              onClick={() => deleteMember(m.id)}
                            >
                              <Trash2 size={16} />
                              <span className="tooltip">Supprimer</span>
                            </button>

                            <button
                              type="button"
                              title="Notes"
                              aria-label="Afficher les notes"
                              className="btn action note"
                              onClick={() => {
                                setSelectedMember(m);
                                loadComments(m.id);
                              }}
                            >
                              <MessageSquare size={16} />
                              <span className="tooltip">Notes</span>
                            </button>

                            <button
                              type="button"
                              title="Voir le dossier"
                              aria-label="Voir le dossier du membre"
                              className="icon-btn note"
                              onClick={() => navigate(`/member/${m.id}`)}
                            >
                              <Eye size={16} />
                              <span className="tooltip">Voir</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                ⬅️
              </button>

              <span>
                Page {page} / {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                ➡️
              </button>
            </div>

            {editing && (
              <div className="modal-overlay" onClick={() => setEditing(null)}>
                <div
                  className="edit-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>Modifier membre</h3>

                  {/* 📸 PHOTO */}
                  <div className="photo-edit">
                    <label>Photo</label>

                    {/* PREVIEW */}
                    <img
                      src={
                        editing.preview
                          ? editing.preview
                          : editing.photo
                            ? `/api/uploads/${editing.photo}`
                            : "/default-avatar.png"
                      }
                      alt="preview"
                      className="edit-photo-preview"
                    />

                    {/* FILE INPUT */}
                    <input
                      type="file"
                      id="fileUpload"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];

                        if (file) {
                          const reader = new FileReader();

                          reader.onloadend = () => {
                            setEditing({
                              ...editing,
                              newPhoto: file,
                              preview: reader.result,
                            });
                          };

                          reader.readAsDataURL(file);
                        }
                      }}
                    />

                    <label htmlFor="fileUpload" className="upload-btn">
                      📂 Upload
                    </label>

                    {/* 📸 CAMERA BUTTON */}
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => startCamera(videoRef, setCameraOn)}
                    >
                      📸 Camera
                    </button>

                    {/* CAMERA VIEW */}
                    {cameraOn && (
                      <div className="camera-box">
                        <video ref={videoRef} autoPlay playsInline />

                        <div className="camera-actions">
                          <button
                            type="button"
                            onClick={() =>
                              capturePhoto({
                                videoRef,
                                canvasRef,
                                setCameraOn,
                                onCapture: ({ file, preview }) => {
                                  setEditing((prev) => ({
                                    ...prev,
                                    newPhoto: file,
                                    preview: preview,
                                  }));
                                },
                              })
                            }
                          >
                            📷 Capturer
                          </button>

                          <button
                            type="button"
                            onClick={() => stopCamera(videoRef, setCameraOn)}
                          >
                            ❌ Fermer
                          </button>
                        </div>

                        <canvas ref={canvasRef} style={{ display: "none" }} />
                      </div>
                    )}
                  </div>

                  {/* 📋 FORM */}
                  <div className="edit-grid">
                    <div>
                      <label>Nom</label>
                      <input
                        value={editing.nom || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, nom: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Prénom</label>
                      <input
                        value={editing.prenom || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, prenom: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Sexe</label>
                      <select
                        value={editing.gender || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, gender: e.target.value })
                        }
                      >
                        <option value="">--</option>
                        <option value="masculin">Masculin</option>
                        <option value="feminin">Féminin</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label>Email</label>
                      <input
                        value={editing.email || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, email: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Numéro dossier</label>
                      <input
                        value={editing.numero_dossier || ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            numero_dossier: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label>Téléphone</label>
                      <input
                        value={editing.contact || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, contact: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Adresse</label>
                      <input
                        value={editing.adresse || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, adresse: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Code Postal</label>
                      <input
                        value={editing.codePostal || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, codePostal: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Commune</label>
                      <input
                        value={editing.commune || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, commune: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Métier</label>
                      <input
                        value={editing.metier || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, metier: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Hébergement</label>
                      <select
                        value={editing.hebergement || ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            hebergement: e.target.value,
                          })
                        }
                      >
                        <option value="">--</option>
                        <option value="oui">Oui</option>
                        <option value="non">Non</option>
                      </select>
                    </div>
                    <div className="adhesion-group">
                      <label>Type d’adhésion</label>

                      <div className="adhesion-options">
                        {["adherent", "benevole", "organisateur", "autre"].map(
                          (type) => {
                            const isActive =
                              editing.typeAdhesion?.includes(type);

                            return (
                              <div
                                key={type}
                                className={`adhesion-pill ${isActive ? "active" : ""}`}
                                onClick={() => {
                                  let updated = editing.typeAdhesion || [];

                                  if (isActive) {
                                    updated = updated.filter((t) => t !== type);
                                  } else {
                                    updated = [...updated, type];
                                  }

                                  setEditing({
                                    ...editing,
                                    typeAdhesion: updated,
                                  });
                                }}
                              >
                                {type}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                    <div>
                      <label>📋 Suivi administratif</label>

                      <div
                        className="suivi-input"
                        onClick={() => setShowSuiviPopup(true)}
                      >
                        {Array.isArray(editing.suivi_administratif) &&
                        editing.suivi_administratif.length > 0
                          ? editing.suivi_administratif.join(", ")
                          : "Choisir..."}
                      </div>
                    </div>

                    <div>
                      <label>Date de naissance</label>
                      <input
                        type="date"
                        value={editing.naissance || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, naissance: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Date d'adhésion</label>
                      <input
                        type="date"
                        value={editing.adhesion || ""}
                        onChange={(e) =>
                          setEditing({ ...editing, adhesion: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label>Date d'expiration</label>
                      <input
                        type="date"
                        value={editing.dateExpiration || ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            dateExpiration: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* 🔘 ACTIONS */}
                  <div className="edit-actions">
                    <button
                      onClick={async () => {
                        const toastId = toast.loading("Mise à jour...");

                        const updatedMember = { ...editing }; // copy BEFORE closing

                        // ✅ 1. Close modal immediately
                        setEditing(null);

                        // ✅ 2. Instant UI update (VERY IMPORTANT)
                        setMembers((prev) =>
                          prev.map((m) =>
                            m.id === updatedMember.id
                              ? { ...m, ...updatedMember }
                              : m,
                          ),
                        );

                        try {
                          const formData = new FormData();

                          formData.append("id", updatedMember.id);

                          formData.append("nom", updatedMember.nom || "");
                          formData.append("prenom", updatedMember.prenom || "");
                          formData.append("gender", updatedMember.gender || "");
                          formData.append("email", updatedMember.email || "");
                          formData.append(
                            "contact",
                            updatedMember.contact || "",
                          );
                          formData.append(
                            "adresse",
                            updatedMember.adresse || "",
                          );
                          formData.append(
                            "codePostal",
                            updatedMember.codePostal || "",
                          );
                          formData.append(
                            "commune",
                            updatedMember.commune || "",
                          );
                          formData.append("metier", updatedMember.metier || "");
                          formData.append(
                            "hebergement",
                            updatedMember.hebergement || "",
                          );

                          // ✅ TYPE ADHESION FIX
                          formData.append(
                            "typeAdhesion",
                            JSON.stringify(updatedMember.typeAdhesion || []),
                          );

                          // ✅ DOSSIER
                          formData.append(
                            "numero_dossier",
                            updatedMember.numero_dossier || "",
                          );

                          // ✅ DATES
                          formData.append(
                            "naissance",
                            updatedMember.naissance || "",
                          );
                          formData.append(
                            "adhesion",
                            updatedMember.adhesion || "",
                          );
                          formData.append(
                            "dateExpiration",
                            updatedMember.dateExpiration || "",
                          );

                          // ✅ SUIVI
                          formData.append(
                            "suivi_administratif",
                            JSON.stringify(
                              updatedMember.suivi_administratif || [],
                            ),
                          );
                          // PHOTO
                          if (updatedMember.newPhoto) {
                            formData.append("photo", updatedMember.newPhoto);
                          }

                          console.log("SENDING:", updatedMember);
                          const res = await axios.post(
                            "/api/updateMember.php",
                            formData,
                          );

                          console.log("UPDATE RESPONSE:", res.data);

                          if (res.data.success) {
                            toast.success("Membre mis à jour ✅");
                          } else {
                            toast.error(
                              "Erreur: " + (res.data.error || "Unknown"),
                            );
                          }

                          // ✅ 3. Sync with backend (optional but recommended)
                          fetchMembers();
                          fetchStats();
                        } catch (err) {
                          console.error(err);
                          toast.error("Erreur ❌", { id: toastId });

                          // ❌ rollback if API fails
                          fetchMembers();
                        }
                      }}
                    >
                      💾 Save
                    </button>

                    <button onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {showSuiviPopup && (
              <div
                className="modal-overlay"
                onClick={() => setShowSuiviPopup(false)}
              >
                <div
                  className="modal suivi-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>📋 Sélectionner le suivi</h3>

                  <div className="suivi-container">
                    {[
                      "Demandeur d'asile - procédure normale",
                      "Demandeur d'asile - procédure accélérée",
                      "Demandeur d'asile - procédure Dublin",
                      "Sans papiers - pas de démarche en cours",
                      "Sans papiers - demande déposée en ligne",
                      "Sans papiers - demande déposée en préfecture",
                      "Situation régulière",
                      "Situation régulière - renouvellement en cours",
                      "OQTF en cours",
                      "IRTF en cours",
                    ].map((option) => {
                      const current = Array.isArray(editing.suivi_administratif)
                        ? editing.suivi_administratif
                        : [];

                      const isActive = current.includes(option);

                      return (
                        <div
                          key={option}
                          className={`suivi-pill ${isActive ? "active" : ""}`}
                          onClick={() => {
                            let updated;

                            if (isActive) {
                              updated = current.filter((s) => s !== option);
                            } else {
                              updated = [...current, option];
                            }

                            setEditing({
                              ...editing,
                              suivi_administratif: updated,
                            });
                          }}
                        >
                          {option}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="btn primary"
                    onClick={() => setShowSuiviPopup(false)}
                  >
                    ✅ Valider
                  </button>
                </div>
              </div>
            )}

            {selectedMember && (
              <div
                className="modal-overlay"
                onClick={() => setSelectedMember(null)}
                style={{
                  padding: 20,
                  boxSizing: "border-box",
                  overflowY: "auto",
                }}
              >
                <div
                  className="modal-content comment-window"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "min(100%, 1490px)",
                    maxWidth: 1490,
                    height: "calc(100vh - 8px)",
                    maxHeight: "calc(100vh - 8px)",
                    minHeight: 0,
                    boxSizing: "border-box",
                    padding: 24,
                    borderRadius: 16,
                    background: "#fff",
                    color: "#13294b",
                    boxShadow: "0 18px 60px rgba(20, 40, 80, .18)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    overflow: "hidden",
                    fontFamily:
                      "Inter, system-ui, -apple-system, Segoe UI, sans-serif",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      padding: "4px 0 16px",
                      borderBottom: "1px solid #e3e9f3",
                      marginBottom: 16,
                      flex: "0 0 auto",
                    }}
                  >
                    <div
                      style={{
                        width: 74,
                        height: 74,
                        minWidth: 74,
                        borderRadius: "50%",
                        background: "#e9edff",
                        display: "grid",
                        placeItems: "center",
                        color: "#405a9b",
                        overflow: "hidden",
                        border: "1px solid #dce4fb",
                      }}
                    >
                      {selectedMember.photo ? (
                        <img
                          src={`/api/image.php?file=${encodeURIComponent(selectedMember.photo)}`}
                          alt={`Photo de ${selectedMember.nom || ""} ${selectedMember.prenom || ""}`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback = e.currentTarget.nextElementSibling;
                            if (fallback) fallback.style.display = "block";
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : null}
                      <svg
                        width="42"
                        height="42"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                        style={{
                          display: selectedMember.photo ? "none" : "block",
                        }}
                      >
                        <circle cx="12" cy="7" r="4.2" />
                        <path d="M3.5 21c.4-4.6 3.6-7.2 8.5-7.2s8.1 2.6 8.5 7.2z" />
                      </svg>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h2
                        style={{
                          margin: 0,
                          fontSize: "clamp(21px, 3vw, 32px)",
                          lineHeight: 1.2,
                          letterSpacing: ".2px",
                          fontWeight: 800,
                          color: "#10284d",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {selectedMember.nom} {selectedMember.prenom}
                      </h2>
                      <div
                        style={{
                          marginTop: 5,
                          fontSize: "clamp(15px, 2vw, 21px)",
                          color: "#8190ad",
                        }}
                      >
                        Commentaires et suivi du dossier
                      </div>
                    </div>
                  </div>

                  <div
                    className="comments-list"
                    style={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                      padding: "0 12px 0 38px",
                      marginBottom: 16,
                      flex: "1 1 0px",
                      height: "100%",
                      maxHeight: "none",
                      minHeight: 0,
                      overflowY: "auto",
                      overscrollBehavior: "contain",
                      scrollbarGutter: "stable",
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        top: 22,
                        bottom: 0,
                        left: 10,
                        width: 3,
                        borderRadius: 4,
                        background: "#e7ecf8",
                      }}
                    />
                    {Array.isArray(comments) && comments.length > 0 ? (
                      comments.map((c, index) => {
                        const commentId = String(
                          c.id ?? c.comment_id ?? c.created_at ?? index,
                        );
                        const translation =
                          commentTranslations[commentId] || {};
                        const selectedLanguage =
                          translation.selectedLanguage || "original";
                        const displayedComment =
                          selectedLanguage === "original"
                            ? c.commentaire
                            : translation[selectedLanguage] || c.commentaire;
                        const author =
                          c.author_name ||
                          c.admin_name ||
                          c.created_by_name ||
                          "Administrateur";
                        const activeButton = (active) => ({
                          border: "1px solid transparent",
                          borderRadius: 6,
                          padding: "4px 6px",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 10,
                          display: "inline-flex",
                          flex: "0 0 auto",
                          width: "max-content",
                          minWidth: "max-content",
                          maxWidth: "max-content",
                          alignSelf: "flex-start",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          whiteSpace: "nowrap",
                          gap: 3,
                          lineHeight: 1.2,
                          background: active ? "#1554ed" : "#edf0f6",
                          color: active ? "#fff" : "#1b2f50",
                          boxShadow: active
                            ? "0 2px 5px rgba(21,84,237,.14)"
                            : "none",
                        });
                        return (
                          <div
                            key={c.id || index}
                            className="comment-item"
                            style={{
                              position: "relative",
                              background:
                                "linear-gradient(110deg, #f3f6fc 0%, #f8faff 100%)",
                              borderRadius: 16,
                              padding: "12px 16px",
                              boxSizing: "border-box",
                              minWidth: 0,
                              flex: "0 0 auto",
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                position: "absolute",
                                left: -36,
                                top: 26,
                                width: 17,
                                height: 17,
                                borderRadius: "50%",
                                background: index === 0 ? "#1554ed" : "#7b879b",
                                border: "2px solid white",
                                boxSizing: "content-box",
                                zIndex: 1,
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                marginBottom: 10,
                                color: "#7887a4",
                                fontSize: 13,
                                flexWrap: "wrap",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  flexWrap: "wrap",
                                }}
                              >
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  aria-hidden="true"
                                >
                                  <rect
                                    x="3"
                                    y="5"
                                    width="18"
                                    height="16"
                                    rx="2"
                                  />
                                  <path d="M16 3v4M8 3v4M3 10h18" />
                                </svg>
                                <span>
                                  {new Date(c.created_at).toLocaleString()}
                                </span>
                                <span style={{ color: "#a4aec1" }}>·</span>
                                <strong
                                  style={{ color: "#435675", fontWeight: 700 }}
                                >
                                  {author}
                                </strong>
                              </div>
                              <span
                                aria-label="Options du commentaire"
                                title="Options du commentaire"
                                style={{
                                  color: "#7c879d",
                                  fontSize: 24,
                                  lineHeight: 1,
                                  letterSpacing: 2,
                                }}
                              >
                                ⋮
                              </span>
                            </div>
                            {Number(c.is_deleted) === 1 ? (
                              <div
                                style={{
                                  color: "#9b2634",
                                  fontSize: 13,
                                  fontStyle: "italic",
                                  lineHeight: 1.45,
                                }}
                              >
                                Commentaire supprimé par{" "}
                                {c.deleted_by || "un administrateur"}
                                {c.deleted_at
                                  ? ` · ${new Date(c.deleted_at).toLocaleString("fr-FR")}`
                                  : ""}
                                . L’historique est conservé.
                              </div>
                            ) : (
                              <div
                                className="comment-text"
                                style={{
                                  color: "#162849",
                                  fontSize: "clamp(14px, 1.2vw, 17px)",
                                  lineHeight: 1.45,
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {displayedComment}
                              </div>
                            )}
                            {c.updated_at &&
                              c.updated_at !== c.created_at &&
                              Number(c.is_deleted) !== 1 && (
                                <div
                                  style={{
                                    marginTop: 7,
                                    color: "#687b9b",
                                    fontSize: 11,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  Modifié par{" "}
                                  {c.edited_by || "un administrateur"} ·{" "}
                                  {new Date(c.updated_at).toLocaleString(
                                    "fr-FR",
                                  )}
                                </div>
                              )}
                            {Array.isArray(c.history) &&
                              c.history.length > 0 && (
                                <details
                                  style={{
                                    marginTop: 7,
                                    color: "#435675",
                                    fontSize: 11,
                                  }}
                                >
                                  <summary
                                    style={{
                                      cursor: "pointer",
                                      fontWeight: 700,
                                    }}
                                  >
                                    Voir l’historique des modifications (
                                    {c.history.length})
                                  </summary>
                                  <div
                                    style={{
                                      display: "grid",
                                      gap: 6,
                                      marginTop: 6,
                                    }}
                                  >
                                    {c.history.map((h, hi) => (
                                      <div
                                        key={h.id || hi}
                                        style={{
                                          padding: 8,
                                          background: "#fff",
                                          border: "1px solid #e0e7f2",
                                          borderRadius: 7,
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: "#687b9b",
                                            marginBottom: 4,
                                          }}
                                        >
                                          {h.edited_by || "Administrateur"} ·{" "}
                                          {h.edited_at
                                            ? new Date(
                                                h.edited_at,
                                              ).toLocaleString("fr-FR")
                                            : ""}
                                        </div>
                                        <div>
                                          <strong>Avant :</strong> {h.old_text}
                                        </div>
                                        <div>
                                          <strong>Après :</strong> {h.new_text}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            <div
                              className="comment-actions"
                              style={{
                                display: "flex",
                                gap: 9,
                                flexWrap: "wrap",
                                marginTop: 10,
                              }}
                            >
                              <button
                                type="button"
                                style={activeButton(
                                  selectedLanguage === "original",
                                )}
                                onClick={() =>
                                  setCommentTranslations((prev) => ({
                                    ...prev,
                                    [commentId]: {
                                      ...prev[commentId],
                                      selectedLanguage: "original",
                                    },
                                  }))
                                }
                                aria-pressed={selectedLanguage === "original"}
                              >
                                ▤ <span>Original</span>
                              </button>
                              <button
                                type="button"
                                style={activeButton(selectedLanguage === "en")}
                                onClick={() => translateComment(c, "en")}
                                disabled={!!translatingComments[commentId]}
                                aria-pressed={selectedLanguage === "en"}
                              >
                                <svg
                                  aria-hidden="true"
                                  viewBox="0 0 60 30"
                                  width="16"
                                  height="11"
                                  style={{
                                    display: "inline-block",
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    boxShadow: "0 0 0 1px rgba(15,23,42,.12)",
                                  }}
                                >
                                  <clipPath id={`uk-flag-clip-${commentId}`}>
                                    <rect width="60" height="30" rx="2" />
                                  </clipPath>
                                  <g
                                    clipPath={`url(#uk-flag-clip-${commentId})`}
                                  >
                                    <path fill="#012169" d="M0 0h60v30H0z" />
                                    <path
                                      stroke="#fff"
                                      strokeWidth="6"
                                      d="M0 0l60 30M60 0L0 30"
                                    />
                                    <path
                                      stroke="#C8102E"
                                      strokeWidth="2"
                                      d="M0 0l60 30M60 0L0 30"
                                    />
                                    <path
                                      fill="#fff"
                                      d="M25 0h10v30H25zM0 10h60v10H0z"
                                    />
                                    <path
                                      fill="#C8102E"
                                      d="M27 0h6v30h-6zM0 12h60v6H0z"
                                    />
                                  </g>
                                </svg>
                                <span>
                                  {translatingComments[commentId] &&
                                  selectedLanguage !== "en"
                                    ? "Translating…"
                                    : "EN"}
                                </span>
                              </button>
                              <button
                                type="button"
                                style={activeButton(selectedLanguage === "fr")}
                                onClick={() => translateComment(c, "fr")}
                                disabled={!!translatingComments[commentId]}
                                aria-pressed={selectedLanguage === "fr"}
                              >
                                <svg
                                  aria-hidden="true"
                                  viewBox="0 0 3 2"
                                  width="16"
                                  height="11"
                                  style={{
                                    display: "inline-block",
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    boxShadow: "0 0 0 1px rgba(15,23,42,.12)",
                                  }}
                                >
                                  <path fill="#0055A4" d="M0 0h1v2H0z" />
                                  <path fill="#fff" d="M1 0h1v2H1z" />
                                  <path fill="#EF4135" d="M2 0h1v2H2z" />
                                </svg>
                                <span>
                                  {translatingComments[commentId] &&
                                  selectedLanguage !== "fr"
                                    ? "Traduction…"
                                    : "FR"}
                                </span>
                              </button>
                              {c.id && Number(c.is_deleted) !== 1 && (
                                <>
                                  <button
                                    type="button"
                                    style={activeButton(false)}
                                    onClick={() => {
                                      setEditingComment(c);
                                      setEditedCommentText(c.commentaire || "");
                                    }}
                                    title="Modifier ce commentaire"
                                  >
                                    ✏️ <span>Modifier</span>
                                  </button>
                                  <button
                                    type="button"
                                    style={{
                                      ...activeButton(false),
                                      background: "#fff0f1",
                                      color: "#b42332",
                                      border: "1px solid #f1cbd0",
                                    }}
                                    onClick={async () => {
                                      if (
                                        !window.confirm(
                                          "Supprimer ce commentaire ?",
                                        )
                                      )
                                        return;
                                      try {
                                        await axios.post(
                                          "/api/deleteComment.php",
                                          {
                                            comment_id: c.id,
                                            admin_name:
                                              admin?.name ||
                                              admin?.email ||
                                              "Administrateur",
                                          },
                                        );
                                        toast.success("Commentaire supprimé");
                                        await loadComments(selectedMember.id);
                                      } catch (err) {
                                        console.error(err);
                                        toast.error(
                                          "Impossible de supprimer le commentaire",
                                        );
                                      }
                                    }}
                                    title="Supprimer ce commentaire"
                                  >
                                    🗑️ <span>Supprimer</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p style={{ color: "#7d8aa3", padding: 20 }}>
                        Aucun commentaire
                      </p>
                    )}
                  </div>

                  {editingComment && (
                    <div
                      style={{
                        border: "1px solid #cbd8f0",
                        background: "#f7f9ff",
                        borderRadius: 10,
                        padding: 10,
                        margin: "0 0 10px",
                        flex: "0 0 auto",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          marginBottom: 6,
                          color: "#10284d",
                          fontSize: 13,
                        }}
                      >
                        Modifier le commentaire
                      </strong>
                      <textarea
                        value={editedCommentText}
                        maxLength={1000}
                        onChange={(e) => setEditedCommentText(e.target.value)}
                        style={{
                          width: "100%",
                          minHeight: 64,
                          maxHeight: 100,
                          resize: "vertical",
                          boxSizing: "border-box",
                          border: "1px solid #c5ccda",
                          borderRadius: 8,
                          padding: 9,
                          font: "inherit",
                          fontSize: 14,
                          color: "#172b4d",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          marginTop: 7,
                        }}
                      >
                        <button
                          type="button"
                          style={activeCommentEditButton}
                          onClick={async () => {
                            if (!editedCommentText.trim()) {
                              toast.error(
                                "Le commentaire ne peut pas être vide",
                              );
                              return;
                            }
                            try {
                              await axios.post("/api/editComment.php", {
                                comment_id: editingComment.id,
                                commentaire: editedCommentText.trim(),
                                admin_name:
                                  admin?.name ||
                                  admin?.email ||
                                  "Administrateur",
                              });
                              toast.success("Commentaire modifié");
                              setEditingComment(null);
                              setEditedCommentText("");
                              await loadComments(selectedMember.id);
                            } catch (err) {
                              console.error(err);
                              toast.error(
                                "Impossible de modifier le commentaire",
                              );
                            }
                          }}
                        >
                          💾 Enregistrer
                        </button>
                        <button
                          type="button"
                          style={{
                            ...activeCommentEditButton,
                            background: "#edf0f6",
                            color: "#1b2f50",
                          }}
                          onClick={() => {
                            setEditingComment(null);
                            setEditedCommentText("");
                          }}
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}

                  <textarea
                    className="comment-input"
                    maxLength={1000}
                    placeholder="Ajouter un commentaire..."
                    value={commentText}
                    onChange={(e) => {
                      setCommentText(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    style={{
                      width: "100%",
                      minHeight: 76,
                      height: 76,
                      maxHeight: 140,
                      flex: "0 0 auto",
                      resize: "vertical",
                      boxSizing: "border-box",
                      border: "1.5px solid #c5ccda",
                      borderRadius: 14,
                      padding: "14px 18px",
                      font: "inherit",
                      fontSize: 16,
                      color: "#172b4d",
                      outlineColor: "#1554ed",
                      background: "#fff",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      color: "#8794ad",
                      fontSize: 16,
                      marginTop: 5,
                      marginBottom: 10,
                      flex: "0 0 auto",
                    }}
                  >
                    {commentText.length} / 1000
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      flexWrap: "wrap",
                      alignItems: "center",
                      flex: "0 0 auto",
                      paddingBottom: 2,
                    }}
                  >
                    <button
                      style={{
                        border: 0,
                        borderRadius: 12,
                        padding: "15px 22px",
                        background: "#e9edff",
                        color: "#0752ed",
                        fontSize: 18,
                        fontWeight: 750,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                      onClick={async () => {
                        if (!commentText.trim()) return;
                        const toastId = toast.loading("Ajout...");
                        try {
                          await axios.post("/api/addComment.php", {
                            id: selectedMember.id,
                            commentaire: commentText,
                          });
                          toast.success("Commentaire ajouté 💬", {
                            id: toastId,
                          });
                          setCommentText("");
                          loadComments(selectedMember.id);
                        } catch (err) {
                          console.error(err);
                          toast.error("Erreur ajout ❌", { id: toastId });
                        }
                      }}
                    >
                      <span style={{ fontSize: 28, lineHeight: 0.7 }}>＋</span>{" "}
                      Ajouter
                    </button>
                    <button
                      onClick={() => setSelectedMember(null)}
                      style={{
                        border: 0,
                        borderRadius: 12,
                        padding: "15px 22px",
                        background: "#f0f2f6",
                        color: "#142849",
                        fontSize: 18,
                        fontWeight: 750,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 27, lineHeight: 0.7 }}>×</span>{" "}
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showRangeModal && rangeStats && (
              <div
                className="modal-overlay"
                onClick={() => setShowRangeModal(false)}
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2>📊 Analyse période</h2>

                  <p
                    className="stat-item green"
                    onClick={() => {
                      setSelectedList(rangeStats?.lists?.newMembers || []);
                      setListTitle("🆕 Nouveaux membres");
                    }}
                  >
                    🆕 Nouveaux membres: {rangeStats.newMembers}
                  </p>

                  {/* 🔄 TRANSITIONS ========================= */}
                  {rangeStats?.transitions ? (
                    Object.keys(rangeStats.transitions).length > 0 ? (
                      <div style={{ marginTop: 20 }}>
                        <h3>🔄 Historique de statut</h3>

                        {Object.entries(rangeStats.transitions).map(
                          ([pattern, data]) => {
                            if (!data || !Array.isArray(data.members))
                              return null;

                            return (
                              <p
                                key={pattern}
                                className="stat-item"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setSelectedList(data.members);
                                  setListTitle(
                                    `🔄 ${formatPattern(pattern)} (${data.count || 0})`,
                                  );
                                }}
                              >
                                {formatPattern(pattern)} :{" "}
                                <strong>{data.count || 0}</strong>
                              </p>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <p>Aucun changement</p>
                    )
                  ) : null}

                  {Array.isArray(selectedList) && (
                    <div style={{ marginTop: 20 }}>
                      <h3>{listTitle}</h3>

                      {selectedList.length === 0 ? (
                        <p>Aucun membre</p>
                      ) : (
                        selectedList.map((m) => (
                          <div key={m.id} className="timeline-card">
                            <h4>
                              {m.nom} {m.prenom}
                            </h4>

                            {m.timeline && (
                              <div className="timeline">
                                {m.timeline.map((t, i) => (
                                  <div
                                    key={i}
                                    className={`timeline-segment ${t.status}`}
                                  >
                                    <div className="timeline-label">
                                      {t.status === "actif" ? "🟢" : "🔴"}
                                    </div>

                                    <div className="timeline-bar" />

                                    <div className="timeline-dates">
                                      {t.from} → {t.to}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <button onClick={() => setShowRangeModal(false)}>
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
}

export default Admin;
